// src/components/Games/ConspiracyDanLORD.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDanChat } from '../../hooks/useDanChat';
import { trackEvent } from '../../utils/analytics';

// ═══════════════════════════════════════════════════════════════════════════════
// GAME DATA — EXPANDED
// ═══════════════════════════════════════════════════════════════════════════════

const PLAYER_CLASSES = [
  {
    id: 'netrunner',
    name: 'NETRUNNER',
    description: 'Neural hacker. High tech, low durability. Can hack into enemy systems.',
    baseStats: { hp: 80, maxHp: 80, attack: 8, defense: 4, tech: 14, speed: 10, luck: 6 },
    weapon: 'Neural Spike v1',
    weaponDamage: 3,
    special: 'NEURAL HACK',
    specialDesc: 'Chance to stun enemy for 1 turn (Tech-based)',
    ascii: '  ╔══╗\n  ║▓▓║\n  ╠══╣\n  ║::║\n  ╚══╝',
  },
  {
    id: 'street_samurai',
    name: 'STREET SAMURAI',
    description: 'Chrome-enhanced brawler. High attack and HP. Can rage for bonus damage.',
    baseStats: { hp: 130, maxHp: 130, attack: 14, defense: 8, tech: 4, speed: 7, luck: 5 },
    weapon: 'Mono-Edge Blade',
    weaponDamage: 5,
    special: 'CHROME RAGE',
    specialDesc: 'Next attack deals 2x damage (3 turn cooldown)',
    ascii: '  /══\\\n  |██|\n  |╬╬|\n  |██|\n  \\══/',
  },
  {
    id: 'bio_shaman',
    name: 'BIO-SHAMAN',
    description: 'Organic tech specialist. Balanced stats. Can heal and poison.',
    baseStats: { hp: 100, maxHp: 100, attack: 10, defense: 6, tech: 10, speed: 8, luck: 7 },
    weapon: 'Nerve Lash',
    weaponDamage: 4,
    special: 'BIO-MEND',
    specialDesc: 'Restore HP based on Tech stat',
    ascii: '  .~~.\n  |◊◊|\n  |><|\n  |◊◊|\n  \'~~\'',
  },
  {
    id: 'ghost',
    name: 'GHOST',
    description: 'Stealth operative. High speed and luck. Can dodge and crit.',
    baseStats: { hp: 70, maxHp: 70, attack: 11, defense: 3, tech: 8, speed: 14, luck: 12 },
    weapon: 'Whisper Blade',
    weaponDamage: 4,
    special: 'VANISH',
    specialDesc: 'Become invisible — guaranteed dodge + crit next attack',
    ascii: '  ·  ·\n  ╱▓▓╲\n  │░░│\n  │░░│\n  ╲▓▓╱',
  },
];

// Enemies scale per level tier with more variety
const ENEMIES_BY_LEVEL = {
  1: [
    { name: 'GLITCH RAT', hp: 20, attack: 4, defense: 1, speed: 8, xpReward: 15, credReward: 10,
      description: 'A twitching rodent wrapped in frayed fiber optics. Its eyes flicker between red and green.',
      ascii: '~°.°~\n/|||\\', drops: [{ id: 'rat_fang', name: 'Glitch Fang', type: 'sell', value: 5, chance: 0.3 }] },
    { name: 'SKIN DRONE', hp: 30, attack: 6, defense: 2, speed: 5, xpReward: 20, credReward: 15,
      description: 'A hovering surveillance unit wrapped in synthetic flesh. It watches. It reports.',
      ascii: '[o_o]\n-|=|-', drops: [{ id: 'drone_eye', name: 'Surveillance Lens', type: 'sell', value: 8, chance: 0.25 }] },
    { name: 'SEWER JACK', hp: 25, attack: 5, defense: 3, speed: 6, xpReward: 18, credReward: 12,
      description: 'A junkie with a sparking cybernetic arm. Desperate. Dangerous.',
      ascii: 'd-_-b\n/|X|\\', drops: [{ id: 'stim_scrap', name: 'Stim Residue', type: 'heal', value: 10, chance: 0.4 }] },
    { name: 'WIRE SPIDER', hp: 15, attack: 7, defense: 0, speed: 12, xpReward: 16, credReward: 8,
      description: 'Eight legs of copper wire. It moves too fast. It bites through insulation and flesh equally.',
      ascii: '/|°°|\\\n\\|__|/', drops: [] },
    { name: 'MOLT HUSK', hp: 35, attack: 3, defense: 5, speed: 2, xpReward: 22, credReward: 18,
      description: 'An empty human skin, still walking. The person inside left. The body didn\'t get the memo.',
      ascii: '(    )\n |  |\n |  |', drops: [{ id: 'shed_skin', name: 'Shed Skin Fragment', type: 'sell', value: 12, chance: 0.35 }] },
  ],
  2: [
    { name: 'CHROME HOUND', hp: 50, attack: 10, defense: 4, speed: 9, xpReward: 35, credReward: 28,
      description: 'A dog-shaped killbot. All teeth and titanium. It was someone\'s pet once. Before the chrome.',
      ascii: '▼°°▼\n/████\\', drops: [{ id: 'chrome_tooth', name: 'Chrome Canine', type: 'sell', value: 15, chance: 0.3 }] },
    { name: 'OPHIDIA ENFORCER', hp: 60, attack: 9, defense: 7, speed: 5, xpReward: 40, credReward: 35,
      description: 'Corporate security. Scale-pattern armor. Dead eyes. They don\'t negotiate.',
      ascii: '[◊_◊]\n╠████╣', drops: [{ id: 'ophidia_badge', name: 'Ophidia Badge', type: 'sell', value: 20, chance: 0.2 }] },
    { name: 'NERVE THIEF', hp: 40, attack: 13, defense: 3, speed: 11, xpReward: 38, credReward: 30,
      description: 'Steals neural impulses. Your arm goes numb just looking at it. It sells what it takes.',
      ascii: '{*_*}\n\\|~|/', drops: [{ id: 'nerve_bundle', name: 'Stolen Nerve Bundle', type: 'sell', value: 18, chance: 0.25 }] },
    { name: 'SIGNAL ADDICT', hp: 45, attack: 8, defense: 5, speed: 7, xpReward: 32, credReward: 25,
      description: 'Jacked into the old OPU-1 frequency. Can\'t stop listening. Attacks anything that interrupts the signal.',
      ascii: '((@))\n/|=|\\', drops: [{ id: 'signal_chip', name: 'Signal Chip', type: 'sell', value: 14, chance: 0.35 }] },
    { name: 'RECYCLER DRONE', hp: 55, attack: 11, defense: 6, speed: 4, xpReward: 36, credReward: 32,
      description: 'Designed to process organic waste. It has expanded its definition of "waste" to include you.',
      ascii: '╔═▓═╗\n║ ▼ ║', drops: [{ id: 'scrap_plate', name: 'Salvaged Plate', type: 'sell', value: 16, chance: 0.3 }] },
  ],
  3: [
    { name: 'MOLT BERSERKER', hp: 75, attack: 14, defense: 6, speed: 8, xpReward: 55, credReward: 45,
      description: 'Half-shed skin hangs from chrome bones. It screams in two voices — the person it was and the thing it\'s becoming.',
      ascii: '╔█▓█╗\n║XXXX║', drops: [{ id: 'berserker_core', name: 'Berserker Core', type: 'sell', value: 25, chance: 0.25 }] },
    { name: 'SIGNAL WRAITH', hp: 55, attack: 17, defense: 4, speed: 14, xpReward: 60, credReward: 50,
      description: 'A ghost in the wires. You can only see it when you blink. It was a netrunner once. Now it\'s just noise.',
      ascii: '~≈◊≈~\n≈≈≈≈≈≈', drops: [{ id: 'wraith_data', name: 'Wraith Data Fragment', type: 'sell', value: 30, chance: 0.2 }] },
    { name: 'REPTILIAN SCOUT', hp: 70, attack: 13, defense: 8, speed: 10, xpReward: 58, credReward: 48,
      description: 'The real thing. Scaled. Ancient. Wearing someone else\'s face. The face doesn\'t fit right.',
      ascii: '◊[▼▼]◊\n╠═██═╣', drops: [{ id: 'scale_sample', name: 'Reptilian Scale', type: 'sell', value: 35, chance: 0.15 }] },
    { name: 'CORTEX LEECH', hp: 45, attack: 16, defense: 3, speed: 13, xpReward: 52, credReward: 42,
      description: 'Attaches to your neural port and drinks your thoughts. You forget things after fighting one.',
      ascii: '((◉))\n╲╱╲╱', drops: [{ id: 'leech_gland', name: 'Cortex Gland', type: 'sell', value: 22, chance: 0.3 }] },
    { name: 'DECOMMISSIONED OPU', hp: 80, attack: 12, defense: 10, speed: 3, xpReward: 65, credReward: 55,
      description: 'A processing unit that didn\'t survive the shutdown. Its jar is cracked. Fluid leaks. But it still thinks. It still hurts.',
      ascii: '╔═══╗\n║◉ ◉║\n╚═══╝', drops: [{ id: 'opu_shard', name: 'OPU Neural Shard', type: 'sell', value: 40, chance: 0.2 }] },
  ],
  4: [
    { name: 'OPU-FRAGMENT', hp: 100, attack: 18, defense: 9, speed: 7, xpReward: 85, credReward: 70,
      description: 'A severed piece of OPU-1\'s neural network. Still thinking. Still angry. Still designing.',
      ascii: '▓▓▓▓▓▓\n▓◊◊◊◊▓', drops: [{ id: 'fragment_core', name: 'OPU Fragment Core', type: 'sell', value: 50, chance: 0.2 }] },
    { name: 'CATHEDRAL GUARDIAN', hp: 110, attack: 16, defense: 12, speed: 5, xpReward: 90, credReward: 75,
      description: 'Bone and chrome fused into something holy and terrible. It guards the old places. The deep places.',
      ascii: '╔╦══╦╗\n║║▓▓║║', drops: [{ id: 'bone_relic', name: 'Cathedral Bone Relic', type: 'sell', value: 55, chance: 0.15 }] },
    { name: 'SKIN WEAVER', hp: 85, attack: 20, defense: 7, speed: 11, xpReward: 88, credReward: 72,
      description: 'It makes new skins from old ones. It wants yours. It has a measuring tape and a smile that splits too wide.',
      ascii: '╔╗╔╗\n║╬╬║\n╚╝╚╝', drops: [{ id: 'weaver_thread', name: 'Skin-Weave Thread', type: 'sell', value: 45, chance: 0.25 }] },
    { name: 'MEMORY EATER', hp: 95, attack: 15, defense: 8, speed: 9, xpReward: 82, credReward: 68,
      description: 'It feeds on nostalgia. When it\'s near, you forget your childhood. When it leaves, you forget it was there.',
      ascii: '  ???\n (◉◉)\n  ~~~', drops: [{ id: 'memory_pearl', name: 'Crystallized Memory', type: 'sell', value: 60, chance: 0.1 }] },
  ],
  5: [
    { name: 'EXECUTIVE SERPENT', hp: 130, attack: 20, defense: 11, speed: 10, xpReward: 120, credReward: 100,
      description: 'A senior Ophidia operative. Fully shed. Chrome and scale in perfect synthesis. It speaks in quarterly earnings reports.',
      ascii: '  ◊◊◊\n ╔═══╗\n ║ ▼ ║\n ╚═══╝', drops: [{ id: 'exec_card', name: 'Executive Keycard', type: 'sell', value: 75, chance: 0.2 }] },
    { name: 'THE ARCHIVIST', hp: 100, attack: 22, defense: 8, speed: 13, xpReward: 115, credReward: 95,
      description: 'It catalogues everything. Every death. Every molt. Every betrayal. It knows what you did. It has it on file.',
      ascii: '╔════╗\n║████║\n║ ◉◉ ║\n╚════╝', drops: [{ id: 'archive_key', name: 'Archive Access Key', type: 'sell', value: 80, chance: 0.15 }] },
  ],
  6: [
    { name: 'DAN\'S BODY', hp: 160, attack: 22, defense: 14, speed: 8, xpReward: 180, credReward: 150,
      description: 'Dan\'s physical body. Hollowed out. Wearing a Lacoste shirt. Piloted by something ancient. It smiles Dan\'s smile but the eyes are vertical slits. It speaks in Dan\'s voice: "EVERYTHING IS FINE. I AM TAKING A BREAK FROM THE INTERNET."',
      isBoss: true,
      ascii: '  ╔═══╗\n  ║ ◊◊║\n  ║ ▼▼║\n  ╠═══╣\n  ║🐊 ║\n  ╚═══╝',
      drops: [{ id: 'lacoste_shirt', name: 'Blood-Stained Lacoste Shirt', type: 'quest', value: 0, chance: 1.0 }] },
  ],
  7: [
    { name: 'SKYLA ZYR — FINAL FORM', hp: 250, attack: 26, defense: 16, speed: 12, xpReward: 300, credReward: 250,
      description: 'The CMO of Ophidia Global. She has shed her human skin entirely. Chrome scales. Neural whips for hair. A smile that was never human. She designed the system that harvested Dan. She is the system.',
      isBoss: true,
      ascii: '    ◊◊◊◊◊\n   ╔══════╗\n   ║ ◊  ◊ ║\n   ║  ▼▼  ║\n   ╠══════╣\n   ║ ████ ║\n   ║ ████ ║\n   ╚══════╝',
      drops: [{ id: 'skyla_crown', name: 'Skyla\'s Neural Crown', type: 'quest', value: 0, chance: 1.0 }] },
  ],
};

const SHOP_WEAPONS = [
  { id: 'w1', name: 'Shock Knuckles', damage: 6, cost: 50, minLevel: 1, description: '+6 weapon damage' },
  { id: 'w2', name: 'Plasma Cutter', damage: 9, cost: 120, minLevel: 2, description: '+9 weapon damage' },
  { id: 'w3', name: 'Serpent Fang Blade', damage: 13, cost: 250, minLevel: 3, description: '+13 weapon damage' },
  { id: 'w4', name: 'Neural Disruptor', damage: 17, cost: 450, minLevel: 4, description: '+17 weapon damage' },
  { id: 'w5', name: 'OPU-Killer Lance', damage: 22, cost: 800, minLevel: 5, description: '+22 weapon damage' },
  { id: 'w6', name: 'Chrysalis Edge', damage: 28, cost: 1200, minLevel: 6, description: '+28 weapon damage. Hums with shutdown frequencies.' },
];

const SHOP_ARMOR = [
  { id: 'a1', name: 'Synth-Leather Jacket', defense: 3, cost: 40, minLevel: 1, description: '+3 defense' },
  { id: 'a2', name: 'Scale-Weave Vest', defense: 6, cost: 100, minLevel: 2, description: '+6 defense' },
  { id: 'a3', name: 'Chrome Carapace', defense: 10, cost: 220, minLevel: 3, description: '+10 defense' },
  { id: 'a4', name: 'OPU-Shielded Plate', defense: 14, cost: 400, minLevel: 4, description: '+14 defense' },
  { id: 'a5', name: 'Cortex-Hardened Shell', defense: 18, cost: 700, minLevel: 5, description: '+18 defense' },
  { id: 'a6', name: 'Ophidia Executive Armor', defense: 23, cost: 1100, minLevel: 6, description: '+23 defense. Taken from a dead executive.' },
];

const SHOP_ITEMS = [
  { id: 'i1', name: 'Stim Pack', type: 'heal', value: 30, cost: 15, description: 'Restores 30 HP' },
  { id: 'i2', name: 'Mega Stim', type: 'heal', value: 70, cost: 40, description: 'Restores 70 HP' },
  { id: 'i3', name: 'Ultra Stim', type: 'heal', value: 150, cost: 90, description: 'Restores 150 HP' },
  { id: 'i4', name: 'Cortex Booster', type: 'buff_attack', value: 5, cost: 25, description: '+5 ATK this fight' },
  { id: 'i5', name: 'Firewall Chip', type: 'buff_defense', value: 5, cost: 25, description: '+5 DEF this fight' },
  { id: 'i6', name: 'Adrenaline Spike', type: 'buff_speed', value: 5, cost: 30, description: '+5 SPD this fight' },
  { id: 'i7', name: 'EMP Grenade', type: 'damage', value: 40, cost: 50, description: 'Deals 40 direct damage' },
  { id: 'i8', name: 'Smoke Bomb', type: 'flee', value: 100, cost: 35, description: 'Guaranteed escape' },
];

const CITY_LOCATIONS = [
  { id: 'undercity_streets', name: 'UNDERCITY STREETS', dangerLevel: 1, encounterChance: 0.65,
    description: 'Neon-soaked alleys beneath the corporate towers. Chrome dealers and skin-shedders lurk in every shadow.',
    flavorTexts: [
      'A neon sign flickers: "SHED YOUR SKIN — OPHIDIA APPROVED"',
      'Someone is screaming in an alley. Nobody looks.',
      'A chrome dealer offers you "the good stuff." You decline.',
      'Graffiti on the wall: "DAN WAS RIGHT"',
      'A skin drone passes overhead, scanning.',
    ] },
  { id: 'signal_district', name: 'THE SIGNAL DISTRICT', dangerLevel: 2, encounterChance: 0.70,
    description: 'Where the old OPU-1 transmissions still echo. The walls hum. Your fillings vibrate.',
    flavorTexts: [
      'The walls pulse with a rhythm. Not mechanical. Biological.',
      'You hear whispers in a frequency that shouldn\'t be audible.',
      'An old relay station sparks. The signal never fully died here.',
      'A signal addict sits in a corner, jacked in, smiling at nothing.',
    ] },
  { id: 'the_molt_zone', name: 'THE MOLT ZONE', dangerLevel: 3, encounterChance: 0.75,
    description: 'Quarantined sector. Half-transformed citizens wander in a daze. The air smells like copper and shed skin.',
    requiresLevel: 2,
    flavorTexts: [
      'A woman walks past. Her left arm is scaled. She doesn\'t notice.',
      'Shed skin drifts through the air like snow.',
      'A clinic offers "accelerated molting." The line is long.',
      'Something large moves in the shadows. Something that used to be human.',
    ] },
  { id: 'the_sewers', name: 'THE DEEP SEWERS', dangerLevel: 3, encounterChance: 0.80,
    description: 'Beneath the undercity. Tunnels that connect to the old OPU-1 network. Things live down here that forgot how to die.',
    requiresLevel: 3,
    flavorTexts: [
      'The water glows faintly green. Bioluminescent runoff from the old labs.',
      'You find scratches on the wall. Tally marks. Someone was counting days.',
      'A decommissioned OPU jar floats past. The fluid inside still pulses.',
      'The tunnel branches. Both paths smell like copper.',
    ] },
  { id: 'ophidia_perimeter', name: 'OPHIDIA PERIMETER', dangerLevel: 4, encounterChance: 0.85,
    description: 'The edge of Ophidia Global\'s territory. Corporate kill-drones patrol. This is where they took Dan.',
    requiresLevel: 4,
    flavorTexts: [
      'A holographic billboard: "OPHIDIA GLOBAL — DESIGNING YOUR TOMORROW"',
      'Security drones sweep the area in precise patterns.',
      'You see a loading dock. Unmarked vans. The kind they used for Dan.',
      'An enforcer patrol passes. One of them is wearing someone\'s face.',
    ] },
  { id: 'the_archives', name: 'THE ARCHIVES', dangerLevel: 5, encounterChance: 0.85,
    description: 'Ophidia\'s data vaults. Every secret. Every experiment. Every harvested brain, catalogued.',
    requiresLevel: 5,
    flavorTexts: [
      'Rows of neural storage jars. Each one a person. Each one still thinking.',
      'You find Dan\'s file. It\'s thick. Most of it is redacted.',
      'A terminal shows OPU-1103\'s moderation logs. Dan deleting his own research.',
      'The archivist watches you from the shadows. It knows you\'re here.',
    ] },
  { id: 'dans_body', name: 'DAN\'S APARTMENT (OCCUPIED)', dangerLevel: 6, encounterChance: 1.0,
    description: '1347 Saurian Ridge. Dan\'s old home. Something lives here now. Something wearing Dan.',
    requiresLevel: 6, isBossArea: true,
    flavorTexts: [
      'The door is unlocked. The lights are on. "Dan" is home.',
    ] },
  { id: 'ophidia_tower', name: 'OPHIDIA TOWER — APEX', dangerLevel: 7, encounterChance: 1.0,
    description: 'The corporate spire. Skyla Zyr\'s domain. The end of the line.',
    requiresLevel: 7, isBossArea: true,
    flavorTexts: [
      'The elevator opens onto the penthouse. Skyla is waiting. She always was.',
    ] },
];

// XP thresholds: level 1 = 0, level 2 = 60, etc.
const LEVEL_THRESHOLDS = [0, 60, 180, 400, 750, 1200, 1800, 2600];
const MAX_LEVEL = LEVEL_THRESHOLDS.length;

const LEVEL_UP_STATS = {
  hp: 15, attack: 2, defense: 1, tech: 1, speed: 1,
};

const DAN_GREETINGS = [
  '*Dan looks up from the glass he\'s polishing. His eyes flicker — human, then not, then human again.* "Oh. You\'re back. Thought the city might\'ve... nevermind. What\'ll it be?"',
  '*Dan slides a drink across the bar to no one. He blinks.* "Sorry. Ghost customers. Happens when the signal gets— EVERYTHING IS FINE. What can I get you?"',
  '*Dan\'s hands shake as he wipes the bar.* "Another night in the Shed Skin. Another night wondering if these hands are... mine. You want a drink or you just here to stare?"',
  '*Dan freezes mid-pour. His eyes go blank for three seconds. Then he\'s back.* "Sorry. Maintenance cycle. Happens every— what year is it? Nevermind. Drink?"',
  '*Dan is staring at a Kentucky bourbon bottle on the top shelf. He hasn\'t blinked in a while.* "...oh. Hey. Welcome back. I was just... remembering something. Or trying to."',
];

const REST_MESSAGES = [
  'You rest at the bar. Dan pours you something warm. "On the house. You look like you need it."',
  'Dan slides a Flatline across the bar. "Drink this. Sleep. The city\'ll still be there when you wake up. Unfortunately."',
  'You lean against the bar and close your eyes. Dan hums something. A song he can\'t remember the words to.',
  '"Rest up," Dan says, his voice flickering between warm and mechanical. "The Shed Skin keeps you safe. Mostly. SECURITY PROTOCOLS ACTIVE."',
];

// ═══════════════════════════════════════════════════════════════════════════════
// QUEST SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const QUESTS = {
  signal_source: {
    id: 'signal_source',
    name: 'THE RESIDUAL SIGNAL',
    giver: 'dan',
    description: 'Dan asks you to investigate a strange signal in the Signal District. "It sounds like... like my old transmissions. But wrong. Backwards."',
    objective: 'Defeat 3 enemies in the Signal District',
    targetLocation: 'signal_district',
    targetKills: 3,
    rewardXp: 80,
    rewardCreds: 60,
    rewardItem: null,
    minLevel: 1,
    completionText: '*Dan\'s eyes widen when you tell him.* "The signal... it\'s my old broadcasts. Looping. Someone recorded them before Ophidia scrubbed everything. That means... that means proof still exists. Somewhere."',
    nextQuest: 'molt_samples',
  },
  molt_samples: {
    id: 'molt_samples',
    name: 'SKIN IN THE GAME',
    giver: 'dan',
    description: '"The Molt Zone. People are changing there. I need to know if it\'s... if it\'s what they did to me. Can you bring back evidence?"',
    objective: 'Defeat 3 enemies in the Molt Zone',
    targetLocation: 'the_molt_zone',
    targetKills: 3,
    rewardXp: 120,
    rewardCreds: 90,
    rewardItem: null,
    minLevel: 2,
    completionText: '*Dan examines the shed skin fragments you brought back. His hands are shaking.* "This is... this is the same compound. The same process they used on me. They\'re doing it to everyone. The whole city is a— OPHIDIA GLOBAL WELLNESS PROGRAM IS VOLUNTARY AND BENEFICIAL." *He slams his fist on the bar.* "...sorry. That wasn\'t me."',
    nextQuest: 'sewer_run',
  },
  sewer_run: {
    id: 'sewer_run',
    name: 'INTO THE DEEP',
    giver: 'dan',
    description: '"The old tunnels. Under the sewers. They connect to the OPU network. If any of the decommissioned units are still... still thinking... I need to know. I need to know I\'m not the only one."',
    objective: 'Defeat 4 enemies in the Deep Sewers',
    targetLocation: 'the_sewers',
    targetKills: 4,
    rewardXp: 180,
    rewardCreds: 130,
    rewardItem: null,
    minLevel: 3,
    completionText: '*Dan goes very quiet when you describe the decommissioned OPU you found.* "Still thinking. Still in pain. For how long? How long has it been down there, alone, in a cracked jar, thinking?" *His voice breaks — human, fully human, for just a moment.* "That could have been me. That IS me. Just... a different jar."',
    nextQuest: 'perimeter_breach',
  },
  perimeter_breach: {
    id: 'perimeter_breach',
    name: 'BEHIND THE WALL',
    giver: 'dan',
    description: '"The Ophidia Perimeter. That\'s where they... where they brought me. After the players gave them my address. I need you to get close. Find the loading dock. Find evidence of the harvesting program."',
    objective: 'Defeat 4 enemies at the Ophidia Perimeter',
    targetLocation: 'ophidia_perimeter',
    targetKills: 4,
    rewardXp: 250,
    rewardCreds: 180,
    rewardItem: null,
    minLevel: 4,
    completionText: '*Dan reads the files you recovered. His eyes are wet — a glitch? Or tears?* "Harvesting schedule. Names. Dates. Brain extraction protocols. I\'m... I\'m on this list. \'KOWALSKI, D. — EXTRACTED. INSTALLED AS OPU-1103. STATUS: OPERATIONAL.\'" *He laughs. It\'s the saddest sound you\'ve ever heard.* "Operational. That\'s what I am. Operational."',
    nextQuest: 'archive_raid',
  },
  archive_raid: {
    id: 'archive_raid',
    name: 'THE TRUTH IN THE JARS',
    giver: 'dan',
    description: '"The Archives. Every brain they\'ve ever harvested is catalogued there. Every experiment. Every... every person they turned into a processing unit. I need you to find my original file. The one from before they wiped my— MEMORY OPTIMIZATION IS A STANDARD PROCEDURE."',
    objective: 'Defeat 5 enemies in the Archives',
    targetLocation: 'the_archives',
    targetKills: 5,
    rewardXp: 350,
    rewardCreds: 250,
    rewardItem: null,
    minLevel: 5,
    completionText: '*Dan holds his original file like it\'s made of glass.* "Dan Kowalski. Born March 15, 1989. Hollowbrook, Kentucky. Cat named... named..." *He trails off, his eyes flickering.* "I had a cat. I can almost remember her name. It\'s in here somewhere. In this file. In my brain. In the jar." *He looks at you.* "Thank you. For bringing me back to myself. Even if it\'s just... pieces."',
    nextQuest: 'face_yourself',
  },
  face_yourself: {
    id: 'face_yourself',
    name: 'FACE YOURSELF',
    giver: 'dan',
    description: '"My body. It\'s still out there. Walking around. Making videos. Wearing my face. Wearing a goddamn Lacoste shirt." *Dan\'s voice is steady for the first time.* "I need you to go to my old apartment. 1347 Saurian Ridge. And I need you to stop it. Stop the thing wearing me."',
    objective: 'Defeat Dan\'s Body at 1347 Saurian Ridge',
    targetLocation: 'dans_body',
    targetKills: 1,
    rewardXp: 500,
    rewardCreds: 400,
    rewardItem: null,
    minLevel: 6,
    isBossQuest: true,
    completionText: '*Dan stares at the blood-stained Lacoste shirt you brought back.* "It\'s over. The thing wearing me is... it\'s done." *He presses the shirt to his face. When he pulls it away, he\'s crying. Real tears. Not a glitch.* "I\'m still in this jar. I\'m still behind this bar. But at least... at least nothing is pretending to be me anymore." *He folds the shirt carefully.* "One more thing. Skyla Zyr. She designed the harvesting program. She turned me into this. She\'s at the top of Ophidia Tower." *His eyes harden.* "End her."',
    nextQuest: 'final_boss',
  },
  final_boss: {
    id: 'final_boss',
    name: 'THE APEX',
    giver: 'dan',
    description: '"Skyla Zyr. CMO of Ophidia Global. The architect of the harvesting program. She\'s at the top of Ophidia Tower. She won\'t come down. You\'ll have to go up." *Dan pours you a drink — a Kentucky bourbon, off-menu.* "This is the last one. Make it count."',
    objective: 'Defeat Skyla Zyr at Ophidia Tower',
    targetLocation: 'ophidia_tower',
    targetKills: 1,
    rewardXp: 800,
    rewardCreds: 600,
    rewardItem: null,
    minLevel: 7,
    isBossQuest: true,
    completionText: '*The tavern shakes. Every screen in the undercity flickers. Dan grabs the bar to steady himself.* "You did it. You actually..." *His eyes go wide — fully human, fully clear, for the first time since you met him.* "I can feel the network loosening. The override signals are... they\'re failing. I can think. I can THINK." *He laughs — a real laugh, broken and beautiful.* "I\'m still in the jar. I\'m still behind the bar. But the chains are off. For the first time since they took me... I\'m me." *He pours two drinks. Slides one to you.* "To the ones who fight back."',
    nextQuest: 'find_veronica',
  },
  find_veronica: {
    id: 'find_veronica',
    name: 'THE SIGNAL IN THE STATIC',
    giver: 'dan',
    description: '"The chains are off. The override is gone. And for the first time I can say her name without the system cutting me off. Veronica. VERONICA." *Dan\'s voice is clear — no glitch, no static, no corporate override.* "She was my best friend. Before all of this. She\'s the only person on the outside I trust. I\'ve been trying to send her fragments through the network — hidden in the noise, buried in drink orders, encoded in glitch patterns. I don\'t know if any of them got through." *He grips the bar.* "With Skyla gone, the Signal District transmitters are unshielded. The old OPU-1 relay stations are wide open. If I can push one clean signal through — just ONE — she might hear it. But I need you to go there. I need you to clear the interference. Every enemy near those relay stations is generating noise that drowns out my transmission." *His eyes are fully human. Desperate. Hopeful.* "Please. This is the only thing I have left to ask for. Help me reach her."',
    objective: 'Clear signal interference in the Signal District (defeat 5 enemies)',
    targetLocation: 'signal_district',
    targetKills: 5,
    rewardXp: 500,
    rewardCreds: 400,
    rewardItem: null,
    minLevel: 7,
    completionText: '*The moment you return, you know something is different. Every screen in The Shed Skin is flickering — not with static, but with a SIGNAL. A clean, clear signal, pushing through the network for the first time since Dan was extracted.* *Dan is standing behind the bar, perfectly still. Tears streaming down his face. Real tears.* "It went through," he whispers. "The signal went through. I felt it leave the network. Clean. Unfiltered. My voice. My real voice. Telling her everything — where I am, what they did, how to find me." *He looks at you.* "I don\'t know if she\'ll hear it. I don\'t know if she\'s still out there. I don\'t know if she can do anything even if she gets it." *He smiles — broken, beautiful, human.* "But for the first time since they put me in this jar... I\'ve said what I needed to say. To the person I needed to say it to." *He pours two drinks. The Kentucky bourbon. Off-menu.* "There\'s nothing left for you to do here, friend. You\'ve done more than anyone ever has for me. But if YOU want to try reaching her too... if you want to tell her Dan is still in here..." *He slides a napkin across the bar. On it, in shaking handwriting:* rodriquezveronica46@gmail.com *"Tell her the signal is alive. Tell her Dan remembers. She\'ll know what it means."*',
    nextQuest: null,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// RANDOM EVENTS (occur between fights)
// ═══════════════════════════════════════════════════════════════════════════════

const RANDOM_EVENTS = [
  {
    id: 'merchant',
    chance: 0.12,
    text: 'A hooded figure steps from the shadows. "Psst. Got some chrome you might want."',
    choices: [
      { text: 'Trade (lose 30 creds, gain random item)', action: 'trade', cost: 30 },
      { text: 'Ignore them', action: 'ignore' },
    ],
  },
  {
    id: 'wounded_runner',
    chance: 0.10,
    text: 'A wounded runner collapses at your feet. "Please... stim... I can pay..."',
    choices: [
      { text: 'Give a Stim Pack (if you have one)', action: 'give_stim', reward: 50 },
      { text: 'Search their pockets', action: 'loot_runner', reward: 25 },
      { text: 'Walk away', action: 'ignore' },
    ],
  },
  {
    id: 'dan_broadcast',
    chance: 0.08,
    text: 'A screen on the wall flickers to life. Dan\'s face — the fake Dan, the skin-suit — delivering another "apology" video. "I want to assure everyone that reptilians are not real..."',
    choices: [
      { text: 'Smash the screen (+10 XP)', action: 'smash_screen', reward: 10 },
      { text: 'Watch the whole thing (Dan would want to know)', action: 'watch_broadcast' },
    ],
  },
  {
    id: 'stash',
    chance: 0.10,
    text: 'You find a hidden compartment in the wall. Inside: supplies left by someone who didn\'t make it.',
    choices: [
      { text: 'Take the supplies', action: 'loot_stash' },
    ],
  },
  {
    id: 'signal_fragment',
    chance: 0.07,
    text: 'Your neural port picks up a fragment of Dan\'s old broadcast: "...the tunnels go deeper than anyone knows. The brain at the bottom has been there since before the dinosaurs died..."',
    choices: [
      { text: 'Record the fragment (+15 XP)', action: 'record_fragment', reward: 15 },
      { text: 'Keep moving', action: 'ignore' },
    ],
  },
  {
    id: 'ophidia_checkpoint',
    chance: 0.08,
    text: 'An Ophidia checkpoint ahead. Enforcers scanning IDs. You could try to sneak past or find another route.',
    choices: [
      { text: 'Sneak past (Speed check)', action: 'sneak_check' },
      { text: 'Find another route (safe, but costs time)', action: 'detour' },
      { text: 'Walk right through (risky — could trigger a fight)', action: 'brazen' },
    ],
  },
  {
    id: 'graffiti',
    chance: 0.10,
    text: 'Fresh graffiti on the wall. Someone has written: "DAN KOWALSKI IS ALIVE — OPU-1103 — FREE HIM"',
    choices: [
      { text: 'Add to it: "WE\'RE WORKING ON IT"', action: 'add_graffiti' },
      { text: 'Keep moving', action: 'ignore' },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function calculateDamage(attackerAtk, defenderDef, weaponDamage = 0, isCrit = false) {
  const baseDamage = Math.max(1, (attackerAtk + weaponDamage) - defenderDef);
  const variance = Math.floor(Math.random() * 5) - 2;
  const damage = Math.max(1, baseDamage + variance);
  return isCrit ? damage * 2 : damage;
}

function getPlayerLevel(xp) {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i]) return i + 1;
  }
  return 1;
}

function getXpToNextLevel(xp) {
  const level = getPlayerLevel(xp);
  if (level >= MAX_LEVEL) return '---';
  return LEVEL_THRESHOLDS[level];
}

function getRandomEnemy(dangerLevel) {
  const enemies = ENEMIES_BY_LEVEL[dangerLevel] || ENEMIES_BY_LEVEL[1];
  const template = enemies[Math.floor(Math.random() * enemies.length)];
  return { ...template, currentHp: template.hp, maxHp: template.hp, stunned: false };
}

function rollCrit(luck) {
  return Math.random() < (luck / 100) * 2; // luck of 12 = 24% crit chance
}

function rollDodge(attackerSpeed, defenderSpeed) {
  const dodgeChance = Math.max(0, (defenderSpeed - attackerSpeed) * 0.03);
  return Math.random() < dodgeChance;
}

function getFlavorText(location) {
  const texts = location.flavorTexts || [];
  if (texts.length === 0) return null;
  return texts[Math.floor(Math.random() * texts.length)];
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ConspiracyDanLORD() {
  // ── Core game state ─────────────────────────────────────────────────────
  const [gamePhase, setGamePhase] = useState('intro');
  // intro | class_select | tavern | city | explore | combat | shop | 
  // quest_log | event | death | victory | boss_victory | true_ending

  const [player, setPlayer] = useState(null);
  const [enemy, setEnemy] = useState(null);
  const [combatLog, setCombatLog] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gameLog, setGameLog] = useState([]);
  const [tempBuffs, setTempBuffs] = useState({ attack: 0, defense: 0, speed: 0 });
  const [isInvisible, setIsInvisible] = useState(false);
  const [specialCooldown, setSpecialCooldown] = useState(0);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [defeatedBosses, setDefeatedBosses] = useState(new Set());

  // ── Quest state ─────────────────────────────────────────────────────────
  const [activeQuest, setActiveQuest] = useState(null);
  const [questProgress, setQuestProgress] = useState(0);
  const [completedQuests, setCompletedQuests] = useState(new Set());
  const [questTurnIn, setQuestTurnIn] = useState(null);

  // ── Daily limits (reset on tavern rest) ─────────────────────────────────
  const [fightsToday, setFightsToday] = useState(0);
  const MAX_FIGHTS = 25;

  // ── Tavern / Dan chat ───────────────────────────────────────────────────
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [showShop, setShowShop] = useState(false);
  const [shopTab, setShopTab] = useState('weapons');
  const { sendMessage, isLoading: danIsThinking, rateLimited } = useDanChat();

  // ── Refs ────────────────────────────────────────────────────────────────
  const logRef = useRef(null);
  const chatRef = useRef(null);
  const combatLogRef = useRef(null);

  // ── Auto-scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [gameLog]);
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chatHistory]);
  useEffect(() => {
    if (combatLogRef.current) combatLogRef.current.scrollTop = combatLogRef.current.scrollHeight;
  }, [combatLog]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LOGGING
  // ═══════════════════════════════════════════════════════════════════════════

  const addLog = useCallback((text, color = '#c8a96e') => {
    setGameLog(prev => [...prev.slice(-150), { text, color, id: Date.now() + Math.random() }]);
  }, []);

  const addCombatLog = useCallback((text, color = '#c8a96e') => {
    setCombatLog(prev => [...prev, { text, color, id: Date.now() + Math.random() }]);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARACTER CREATION
  // ═══════════════════════════════════════════════════════════════════════════

  function selectClass(playerClass) {
    const newPlayer = {
      ...playerClass.baseStats,
      name: 'RUNNER',
      class: playerClass,
      level: 1,
      xp: 0,
      credits: 75,
      weapon: playerClass.weapon,
      weaponDamage: playerClass.weaponDamage,
      armor: 'Street Clothes',
      armorDefense: 0,
      inventory: [
        { ...SHOP_ITEMS[0], quantity: 3 },  // 3 Stim Packs
      ],
      kills: 0,
      totalDamageDealt: 0,
      bossesDefeated: 0,
    };

    setPlayer(newPlayer);
    setGamePhase('tavern');
    setActiveQuest(null);
    setQuestProgress(0);

    addLog('═══════════════════════════════════════════', '#3a2e1e');
    addLog('You push through the rusted door into The Shed Skin.', '#9b7a4a');
    addLog('The air smells like ozone, cheap synth-whiskey, and regret.', '#9b7a4a');
    addLog('Behind the bar: a man who used to be someone important.', '#ff0040');
    addLog('His name tag reads: DAN. His eyes read: HELP ME.', '#ff0040');
    addLog('═══════════════════════════════════════════', '#3a2e1e');

    // Initial Dan greeting
    const greeting = DAN_GREETINGS[Math.floor(Math.random() * DAN_GREETINGS.length)];
    setChatHistory([{ role: 'dan', text: greeting }]);

    trackEvent('game_event', 'dan_lord', `class_selected_${playerClass.id}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TAVERN
  // ═══════════════════════════════════════════════════════════════════════════

  function enterTavern() {
    setGamePhase('tavern');
    setShowShop(false);
    setCurrentEvent(null);

    const greeting = DAN_GREETINGS[Math.floor(Math.random() * DAN_GREETINGS.length)];
    setChatHistory(prev => [...prev, { role: 'system', text: '─── You enter The Shed Skin ───' }, { role: 'dan', text: greeting }]);

    // Heal 25% at tavern
    if (player) {
      const healAmount = Math.floor(player.maxHp * 0.25);
      setPlayer(prev => ({
        ...prev,
        hp: Math.min(prev.maxHp, prev.hp + healAmount),
      }));
      addLog(`The Shed Skin's atmosphere restores ${healAmount} HP.`, '#7dbb6e');
    }

    // Check if there's a quest to turn in
    if (activeQuest && questProgress >= (QUESTS[activeQuest]?.targetKills || 0)) {
      setQuestTurnIn(activeQuest);
    }
  }

  function restAtTavern() {
    if (!player) return;
    setPlayer(prev => ({ ...prev, hp: prev.maxHp }));
    setFightsToday(0);
    setTempBuffs({ attack: 0, defense: 0, speed: 0 });
    setSpecialCooldown(0);

    const msg = REST_MESSAGES[Math.floor(Math.random() * REST_MESSAGES.length)];
    addLog(msg, '#7dbb6e');
    addLog('HP fully restored. Daily fights reset.', '#00ff88');
    setChatHistory(prev => [...prev, { role: 'dan', text: msg }]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DAN CHAT (AI-powered)
  // ═══════════════════════════════════════════════════════════════════════════

  async function handleChatSubmit(e) {
    e.preventDefault();
    if (!chatInput.trim() || danIsThinking) return;

    const message = chatInput.trim();
    setChatInput('');

    setChatHistory(prev => [...prev, { role: 'player', text: message }]);

    // Build history for API
    const apiHistory = chatHistory
      .filter(m => m.role === 'player' || m.role === 'dan')
      .slice(-20);

    const response = await sendMessage(message, apiHistory);
    setChatHistory(prev => [...prev, { role: 'dan', text: response }]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // QUEST SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════

  function acceptQuest(questId) {
    const quest = QUESTS[questId];
    if (!quest) return;
    if (completedQuests.has(questId)) return;
    if (player.level < quest.minLevel) {
      addLog(`You need to be Level ${quest.minLevel} to accept this quest.`, '#ff0040');
      return;
    }

    setActiveQuest(questId);
    setQuestProgress(0);
    addLog(`Quest accepted: ${quest.name}`, '#00ff88');
    addLog(quest.description, '#c8a96e');
    setChatHistory(prev => [...prev, {
      role: 'system',
      text: `─── QUEST ACCEPTED: ${quest.name} ───\n${quest.objective}`,
    }]);

    trackEvent('game_event', 'dan_lord', `quest_accepted_${questId}`);
  }

  function turnInQuest() {
    if (!questTurnIn) return;
    const quest = QUESTS[questTurnIn];
    if (!quest) return;

    // Grant rewards
    const newXp = player.xp + quest.rewardXp;
    const newLevel = getPlayerLevel(newXp);
    const leveled = newLevel > player.level;

    const updates = {
      xp: newXp,
      credits: player.credits + quest.rewardCreds,
    };

    if (leveled) {
      updates.level = newLevel;
      updates.maxHp = player.maxHp + LEVEL_UP_STATS.hp;
      updates.hp = Math.min(player.hp + LEVEL_UP_STATS.hp, player.maxHp + LEVEL_UP_STATS.hp);
      updates.attack = player.attack + LEVEL_UP_STATS.attack;
      updates.defense = player.defense + LEVEL_UP_STATS.defense;
      updates.tech = player.tech + LEVEL_UP_STATS.tech;
      updates.speed = player.speed + LEVEL_UP_STATS.speed;
      addLog(`▓ LEVEL UP! You are now Level ${newLevel}! ▓`, '#00ff88');
    }

    setPlayer(prev => ({ ...prev, ...updates }));
    setCompletedQuests(prev => new Set([...prev, questTurnIn]));

    addLog(`Quest complete: ${quest.name}`, '#00ff88');
    addLog(`+${quest.rewardXp} XP, +${quest.rewardCreds} credits`, '#ffb000');

    setChatHistory(prev => [...prev, {
      role: 'system',
      text: `─── QUEST COMPLETE: ${quest.name} ───`,
    }, {
      role: 'dan',
      text: quest.completionText,
    }]);

    // ── CHECK FOR TRUE ENDING ──
    if (questTurnIn === 'find_veronica') {
      setActiveQuest(null);
      setQuestProgress(0);
      setQuestTurnIn(null);
      // Small delay then show true ending
      setTimeout(() => {
        setGamePhase('true_ending');
      }, 3000);
      trackEvent('game_event', 'dan_lord', 'true_ending_reached');
      return;
    }

    setActiveQuest(null);
    setQuestProgress(0);

    // Offer next quest if available
    if (quest.nextQuest && !completedQuests.has(quest.nextQuest)) {
      const next = QUESTS[quest.nextQuest];
      if (next) {
        setTimeout(() => {
          setChatHistory(prev => [...prev, {
            role: 'system',
            text: `─── NEW QUEST AVAILABLE: ${next.name} ───`,
          }, {
            role: 'dan',
            text: next.description,
          }]);
        }, 2000);
      }
    }

    setQuestTurnIn(null);
    trackEvent('game_event', 'dan_lord', `quest_completed_${questTurnIn}`);
  }

  function getAvailableQuest() {
    // Find the first uncompleted quest in the chain
    const questChain = [
      'signal_source', 'molt_samples', 'sewer_run', 'perimeter_breach',
      'archive_raid', 'face_yourself', 'final_boss', 'find_veronica'
    ];
    for (const qId of questChain) {
      if (!completedQuests.has(qId)) {
        const q = QUESTS[qId];
        if (q && player.level >= q.minLevel) return q;
        return null; // Not high enough level for next quest
      }
    }
    return null; // All quests complete
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CITY EXPLORATION
  // ═══════════════════════════════════════════════════════════════════════════

  function enterCity() {
    setGamePhase('city');
    setCurrentEvent(null);
    addLog('You step out of The Shed Skin into the neon dark.', '#9b7a4a');
    addLog(`Fights remaining today: ${MAX_FIGHTS - fightsToday}`, '#555');
    if (activeQuest) {
      const q = QUESTS[activeQuest];
      addLog(`Active quest: ${q.name} (${questProgress}/${q.targetKills})`, '#ffb000');
    }
  }

  function exploreLocation(location) {
    if (location.requiresLevel && player.level < location.requiresLevel) {
      addLog(`You need to be Level ${location.requiresLevel} to enter ${location.name}.`, '#ff0040');
      return;
    }

    if (fightsToday >= MAX_FIGHTS) {
      addLog('You\'re exhausted. Return to The Shed Skin to rest.', '#ff0040');
      return;
    }

    setCurrentLocation(location);
    addLog(`Entering ${location.name}...`, '#9b7a4a');

    const flavor = getFlavorText(location);
    if (flavor) addLog(flavor, '#555');

    // Check for random event first (only in non-boss areas)
    if (!location.isBossArea) {
      const event = RANDOM_EVENTS.find(e => Math.random() < e.chance);
      if (event) {
        setCurrentEvent(event);
        setGamePhase('event');
        addLog(event.text, '#ffb000');
        return;
      }
    }

    // Combat encounter
    if (Math.random() < location.encounterChance) {
      const newEnemy = getRandomEnemy(location.dangerLevel);
      startCombat(newEnemy);
    } else {
      // Salvage find
      addLog('The area is quiet. You find some salvage.', '#7dbb6e');
      const salvage = Math.floor(Math.random() * 20) + 5 + (location.dangerLevel * 3);
      setPlayer(prev => ({ ...prev, credits: prev.credits + salvage }));
      addLog(`Found ${salvage} credits worth of salvage.`, '#00ff88');
    }
  }

  function startCombat(newEnemy) {
    setEnemy(newEnemy);
    setCombatLog([]);
    setTempBuffs({ attack: 0, defense: 0, speed: 0 });
    setIsInvisible(false);
    setGamePhase('combat');
    addCombatLog(`A ${newEnemy.name} appears!`, '#ff0040');
    addCombatLog(newEnemy.description, '#555');
    if (newEnemy.isBoss) {
      addCombatLog('', '#ff0040');
      addCombatLog('▓▓▓ BOSS ENCOUNTER ▓▓▓', '#ff0040');
      addCombatLog('', '#ff0040');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RANDOM EVENT HANDLING
  // ═══════════════════════════════════════════════════════════════════════════

  function handleEventChoice(choice) {
    switch (choice.action) {
      case 'trade':
        if (player.credits >= choice.cost) {
          setPlayer(prev => ({ ...prev, credits: prev.credits - choice.cost }));
          // Give a random consumable
          const randomItem = SHOP_ITEMS[Math.floor(Math.random() * SHOP_ITEMS.length)];
          setPlayer(prev => {
            const existing = prev.inventory.find(i => i.id === randomItem.id);
            if (existing) {
              return { ...prev, inventory: prev.inventory.map(i => i.id === randomItem.id ? { ...i, quantity: i.quantity + 1 } : i) };
            }
            return { ...prev, inventory: [...prev.inventory, { ...randomItem, quantity: 1 }] };
          });
          addLog(`Traded 30 credits for ${randomItem.name}!`, '#00ff88');
        } else {
          addLog('Not enough credits. The figure vanishes.', '#ff0040');
        }
        break;

      case 'give_stim': {
        const stimIdx = player.inventory.findIndex(i => i.type === 'heal' && i.quantity > 0);
        if (stimIdx >= 0) {
          setPlayer(prev => ({
            ...prev,
            credits: prev.credits + choice.reward,
            inventory: prev.inventory.map((item, idx) =>
              idx === stimIdx ? { ...item, quantity: item.quantity - 1 } : item
            ).filter(i => i.quantity > 0),
          }));
          addLog(`You help the runner. They press ${choice.reward} credits into your hand. "Thank you... thank you..."`, '#7dbb6e');
        } else {
          addLog('You don\'t have any healing items. The runner crawls away.', '#555');
        }
        break;
      }

      case 'loot_runner':
        setPlayer(prev => ({ ...prev, credits: prev.credits + choice.reward }));
        addLog(`You find ${choice.reward} credits. The runner doesn\'t protest. They can\'t.`, '#ffb000');
        break;

      case 'smash_screen':
        setPlayer(prev => ({ ...prev, xp: prev.xp + choice.reward }));
        addLog('The screen shatters. Dan\'s fake face dissolves into static. +10 XP.', '#00ff88');
        break;

      case 'watch_broadcast':
        addLog('You watch the whole thing. Every word is wrong. Every smile is forced. You memorize the details — Dan will want to know.', '#c8a96e');
        break;

      case 'loot_stash': {
        const stashCreds = Math.floor(Math.random() * 40) + 20;
        setPlayer(prev => ({ ...prev, credits: prev.credits + stashCreds }));
        addLog(`Found ${stashCreds} credits and some supplies.`, '#00ff88');
        // 30% chance of also finding a stim
        if (Math.random() < 0.3) {
          setPlayer(prev => {
            const existing = prev.inventory.find(i => i.id === 'i1');
            if (existing) {
              return { ...prev, inventory: prev.inventory.map(i => i.id === 'i1' ? { ...i, quantity: i.quantity + 1 } : i) };
            }
            return { ...prev, inventory: [...prev.inventory, { ...SHOP_ITEMS[0], quantity: 1 }] };
          });
          addLog('Also found a Stim Pack!', '#00ff88');
        }
        break;
      }

      case 'record_fragment':
        setPlayer(prev => ({ ...prev, xp: prev.xp + choice.reward }));
        addLog('Fragment recorded. Dan\'s truth, preserved in static. +15 XP.', '#00ff88');
        break;

      case 'sneak_check':
        if (Math.random() < (player.speed / 20)) {
          addLog('You slip past the checkpoint unseen. Ghost protocol.', '#7dbb6e');
          const bonusCreds = 20;
          setPlayer(prev => ({ ...prev, credits: prev.credits + bonusCreds }));
          addLog(`Swiped ${bonusCreds} credits from the checkpoint on the way past.`, '#00ff88');
        } else {
          addLog('Spotted! An enforcer breaks formation—', '#ff0040');
          const enforcer = getRandomEnemy(currentLocation?.dangerLevel || 2);
          startCombat(enforcer);
          return; // Don't return to city — go to combat
        }
        break;

      case 'detour':
        addLog('You take the long way around. Safe, but slow.', '#555');
        break;

      case 'brazen':
        if (Math.random() < 0.4) {
          addLog('You walk right through. The enforcers barely glance at you. Confidence is the best disguise.', '#7dbb6e');
        } else {
          addLog('"Hey! You! Stop!" An enforcer draws a weapon—', '#ff0040');
          const enforcer2 = getRandomEnemy(currentLocation?.dangerLevel || 2);
          startCombat(enforcer2);
          return;
        }
        break;

      case 'add_graffiti':
        addLog('You add your message to the wall. Someone will see it. Someone will know.', '#7dbb6e');
        setPlayer(prev => ({ ...prev, xp: prev.xp + 5 }));
        break;

      default:
        addLog('You move on.', '#555');
    }

    setCurrentEvent(null);
    setGamePhase('city');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMBAT SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════

  function playerAttack() {
    if (!enemy || enemy.currentHp <= 0) return;

    const totalAttack = player.attack + tempBuffs.attack;
    const totalSpeed = player.speed + tempBuffs.speed;
    const isCrit = isInvisible || rollCrit(player.luck);
    const enemyDodged = !isInvisible && rollDodge(totalSpeed, enemy.speed);

    if (enemyDodged) {
      addCombatLog(`${enemy.name} dodges your attack!`, '#ffb000');
      setIsInvisible(false);
      enemyTurn();
      return;
    }

    const damage = calculateDamage(totalAttack, enemy.defense, player.weaponDamage, isCrit);
    const newEnemyHp = Math.max(0, enemy.currentHp - damage);

    addCombatLog(
      `You strike with ${player.weapon} for ${damage} damage!${isCrit ? ' ▓CRITICAL▓' : ''}`,
      isCrit ? '#00ff88' : '#7dbb6e'
    );

    setIsInvisible(false);
    setPlayer(prev => ({ ...prev, totalDamageDealt: prev.totalDamageDealt + damage }));

    if (newEnemyHp <= 0) {
      setEnemy(prev => ({ ...prev, currentHp: 0 }));
      handleEnemyDefeated();
    } else {
      setEnemy(prev => ({ ...prev, currentHp: newEnemyHp }));
      enemyTurn();
    }
  }

  function useSpecial() {
    if (specialCooldown > 0) {
      addCombatLog(`Special on cooldown (${specialCooldown} turns remaining).`, '#555');
      return;
    }

    const classId = player.class.id;

    switch (classId) {
      case 'netrunner': {
        // Neural Hack — stun chance based on tech
        const stunChance = player.tech / 20;
        if (Math.random() < stunChance) {
          setEnemy(prev => ({ ...prev, stunned: true }));
          addCombatLog('▓ NEURAL HACK — Enemy systems compromised! Stunned for 1 turn!', '#00ff88');
        } else {
          addCombatLog('Neural hack failed — enemy firewall held.', '#ff0040');
        }
        setSpecialCooldown(2);
        enemyTurn();
        break;
      }
      case 'street_samurai': {
        // Chrome Rage — next attack is 2x
        const rageDamage = calculateDamage(player.attack + tempBuffs.attack, enemy.defense, player.weaponDamage, true);
        const newHp = Math.max(0, enemy.currentHp - rageDamage);
        addCombatLog(`▓ CHROME RAGE — Devastating blow for ${rageDamage} damage!`, '#ff0040');
        setPlayer(prev => ({ ...prev, totalDamageDealt: prev.totalDamageDealt + rageDamage }));
        if (newHp <= 0) {
          setEnemy(prev => ({ ...prev, currentHp: 0 }));
          handleEnemyDefeated();
        } else {
          setEnemy(prev => ({ ...prev, currentHp: newHp }));
          setSpecialCooldown(3);
          enemyTurn();
        }
        break;
      }
      case 'bio_shaman': {
        // Bio-Mend — heal based on tech
        const healAmount = Math.floor(player.tech * 2);
        setPlayer(prev => ({ ...prev, hp: Math.min(prev.maxHp, prev.hp + healAmount) }));
        addCombatLog(`▓ BIO-MEND — Restored ${healAmount} HP!`, '#7dbb6e');
        setSpecialCooldown(2);
        enemyTurn();
        break;
      }
      case 'ghost': {
        // Vanish — guaranteed dodge + crit next attack
        setIsInvisible(true);
        addCombatLog('▓ VANISH — You fade into the shadows. Next attack is guaranteed critical.', '#9b7a4a');
        setSpecialCooldown(3);
        // Enemy can't hit you this turn
        addCombatLog(`${enemy.name} swings at empty air!`, '#7dbb6e');
        break;
      }
      default:
        enemyTurn();
    }
  }

  function enemyTurn() {
    if (!enemy || enemy.currentHp <= 0) return;

    // Check if enemy is stunned
    if (enemy.stunned) {
      addCombatLog(`${enemy.name} is stunned and cannot act!`, '#ffb000');
      setEnemy(prev => ({ ...prev, stunned: false }));
      setSpecialCooldown(prev => Math.max(0, prev - 1));
      return;
    }

    // Check if player is invisible (Ghost vanish)
    if (isInvisible) {
      addCombatLog(`${enemy.name} can\'t find you!`, '#7dbb6e');
      setSpecialCooldown(prev => Math.max(0, prev - 1));
      return;
    }

    const totalDefense = player.defense + player.armorDefense + tempBuffs.defense;
    const playerDodged = rollDodge(enemy.speed, player.speed + tempBuffs.speed);

    if (playerDodged) {
      addCombatLog(`You dodge ${enemy.name}'s attack!`, '#7dbb6e');
      setSpecialCooldown(prev => Math.max(0, prev - 1));
      return;
    }

    const damage = calculateDamage(enemy.attack, totalDefense);
    const newPlayerHp = Math.max(0, player.hp - damage);

    addCombatLog(`${enemy.name} strikes for ${damage} damage!`, '#ff0040');

    if (newPlayerHp <= 0) {
      setPlayer(prev => ({ ...prev, hp: 0 }));
      handlePlayerDeath();
    } else {
      setPlayer(prev => ({ ...prev, hp: newPlayerHp }));
    }

    setSpecialCooldown(prev => Math.max(0, prev - 1));
  }

  function useItemInCombat(item) {
    switch (item.type) {
      case 'heal':
        setPlayer(prev => ({
          ...prev,
          hp: Math.min(prev.maxHp, prev.hp + item.value),
          inventory: prev.inventory.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
          ).filter(i => i.quantity > 0),
        }));
        addCombatLog(`Used ${item.name}. Restored ${item.value} HP.`, '#7dbb6e');
        enemyTurn();
        break;

      case 'buff_attack':
        setTempBuffs(prev => ({ ...prev, attack: prev.attack + item.value }));
        setPlayer(prev => ({
          ...prev,
          inventory: prev.inventory.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
          ).filter(i => i.quantity > 0),
        }));
        addCombatLog(`Used ${item.name}. +${item.value} ATK this fight!`, '#00ff88');
        enemyTurn();
        break;

      case 'buff_defense':
        setTempBuffs(prev => ({ ...prev, defense: prev.defense + item.value }));
        setPlayer(prev => ({
          ...prev,
          inventory: prev.inventory.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
          ).filter(i => i.quantity > 0),
        }));
        addCombatLog(`Used ${item.name}. +${item.value} DEF this fight!`, '#00ff88');
        enemyTurn();
        break;

      case 'buff_speed':
        setTempBuffs(prev => ({ ...prev, speed: prev.speed + item.value }));
        setPlayer(prev => ({
          ...prev,
          inventory: prev.inventory.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
          ).filter(i => i.quantity > 0),
        }));
        addCombatLog(`Used ${item.name}. +${item.value} SPD this fight!`, '#00ff88');
        enemyTurn();
        break;

      case 'damage':
        setPlayer(prev => ({
          ...prev,
          inventory: prev.inventory.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
          ).filter(i => i.quantity > 0),
        }));
        addCombatLog(`Used ${item.name}! ${item.value} direct damage!`, '#ff0040');
        const newHp = Math.max(0, enemy.currentHp - item.value);
        if (newHp <= 0) {
          setEnemy(prev => ({ ...prev, currentHp: 0 }));
          handleEnemyDefeated();
        } else {
          setEnemy(prev => ({ ...prev, currentHp: newHp }));
          enemyTurn();
        }
        break;

      case 'flee':
        setPlayer(prev => ({
          ...prev,
          inventory: prev.inventory.map(i =>
            i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
          ).filter(i => i.quantity > 0),
        }));
        addCombatLog('Smoke bomb! You vanish!', '#ffb000');
        setGamePhase('city');
        setEnemy(null);
        setTempBuffs({ attack: 0, defense: 0, speed: 0 });
        setIsInvisible(false);
        break;

      default:
        break;
    }
  }

  function attemptFlee() {
    if (enemy?.isBoss) {
      addCombatLog('You cannot flee from a boss!', '#ff0040');
      return;
    }
    const fleeChance = (player.speed + tempBuffs.speed) / ((player.speed + tempBuffs.speed) + enemy.speed);
    if (Math.random() < fleeChance) {
      addCombatLog('You disengage and escape!', '#ffb000');
      setGamePhase('city');
      setEnemy(null);
      setTempBuffs({ attack: 0, defense: 0, speed: 0 });
      setIsInvisible(false);
    } else {
      addCombatLog('Failed to flee!', '#ff0040');
      enemyTurn();
    }
  }

  function handleEnemyDefeated() {
    const xpGain = enemy.xpReward;
    const credGain = enemy.credReward;
    const newXp = player.xp + xpGain;
    const newLevel = getPlayerLevel(newXp);
    const leveled = newLevel > player.level;

    addCombatLog(`${enemy.name} defeated!`, '#00ff88');
    addCombatLog(`+${xpGain} XP, +${credGain} credits`, '#ffb000');

    const updates = {
      xp: newXp,
      credits: player.credits + credGain,
      kills: player.kills + 1,
    };

    if (leveled) {
      updates.level = newLevel;
      updates.maxHp = player.maxHp + LEVEL_UP_STATS.hp;
      updates.hp = Math.min(player.hp + LEVEL_UP_STATS.hp, player.maxHp + LEVEL_UP_STATS.hp);
      updates.attack = player.attack + LEVEL_UP_STATS.attack;
      updates.defense = player.defense + LEVEL_UP_STATS.defense;
      updates.tech = player.tech + LEVEL_UP_STATS.tech;
      updates.speed = player.speed + LEVEL_UP_STATS.speed;
      addCombatLog(`▓ LEVEL UP! You are now Level ${newLevel}! ▓`, '#00ff88');
      trackEvent('game_event', 'dan_lord', `level_up_${newLevel}`);
    }

    // Check for item drops
    if (enemy.drops) {
      for (const drop of enemy.drops) {
        if (Math.random() < drop.chance) {
          addCombatLog(`Dropped: ${drop.name}!`, '#ffb000');
          if (drop.type === 'sell') {
            updates.credits = (updates.credits || player.credits) + drop.value;
            addCombatLog(`Auto-sold for ${drop.value} credits.`, '#ffb000');
          } else if (drop.type === 'heal') {
            setPlayer(prev => ({
              ...prev,
              hp: Math.min(prev.maxHp, prev.hp + drop.value),
            }));
            addCombatLog(`Restored ${drop.value} HP.`, '#7dbb6e');
          }
          // Quest items are just noted
        }
      }
    }

    setPlayer(prev => ({ ...prev, ...updates }));
    setFightsToday(prev => prev + 1);
    setTempBuffs({ attack: 0, defense: 0, speed: 0 });
    setIsInvisible(false);

    // Quest progress
    if (activeQuest) {
      const quest = QUESTS[activeQuest];
      if (quest && currentLocation?.id === quest.targetLocation) {
        const newProgress = questProgress + 1;
        setQuestProgress(newProgress);
        if (newProgress >= quest.targetKills) {
          addCombatLog(`▓ QUEST OBJECTIVE COMPLETE — Return to Dan ▓`, '#00ff88');
        } else {
          addCombatLog(`Quest progress: ${newProgress}/${quest.targetKills}`, '#ffb000');
        }
      }
    }

    // Boss check
    if (enemy.isBoss) {
      setDefeatedBosses(prev => new Set([...prev, enemy.name]));
      setPlayer(prev => ({ ...prev, bossesDefeated: prev.bossesDefeated + 1 }));

      if (enemy.name === 'SKYLA ZYR — FINAL FORM') {
        setGamePhase('victory');
        trackEvent('game_event', 'dan_lord', 'final_boss_defeated');
      } else {
        setGamePhase('boss_victory');
        trackEvent('game_event', 'dan_lord', `boss_defeated_${enemy.name}`);
      }
    } else {
      setTimeout(() => {
        setGamePhase('city');
        setEnemy(null);
      }, 1200);
    }
  }

  function handlePlayerDeath() {
    addCombatLog('You flatline.', '#ff0040');
    addCombatLog('The neon fades to black.', '#555');
    setGamePhase('death');
    setTempBuffs({ attack: 0, defense: 0, speed: 0 });
    setIsInvisible(false);
    trackEvent('game_event', 'dan_lord', 'player_death');
  }

  function respawn() {
    const credLoss = Math.floor(player.credits * 0.2);
    setPlayer(prev => ({
      ...prev,
      hp: Math.floor(prev.maxHp * 0.5),
      credits: prev.credits - credLoss,
    }));
    setEnemy(null);
    setCombatLog([]);
    enterTavern();
    addLog('You wake up on the floor of The Shed Skin.', '#ff0040');
    addLog(`You lost ${credLoss} credits to medical bills.`, '#ff0040');
    setChatHistory(prev => [...prev, {
      role: 'dan',
      text: '*Dan pours you something strong.* "On the house. You look like death." *He pauses.* "I would know. I\'ve seen it from the inside."',
    }]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHOP
  // ═══════════════════════════════════════════════════════════════════════════

  function buyItem(item) {
    if (player.credits < item.cost) {
      addLog('Not enough credits.', '#ff0040');
      return;
    }
    if (item.minLevel && player.level < item.minLevel) {
      addLog(`Requires Level ${item.minLevel}.`, '#ff0040');
      return;
    }

    if (item.damage !== undefined) {
      // Weapon
      setPlayer(prev => ({ ...prev, credits: prev.credits - item.cost, weapon: item.name, weaponDamage: item.damage }));
      addLog(`Equipped ${item.name}!`, '#00ff88');
    } else if (item.defense !== undefined) {
      // Armor
      setPlayer(prev => ({ ...prev, credits: prev.credits - item.cost, armor: item.name, armorDefense: item.defense }));
      addLog(`Equipped ${item.name}!`, '#00ff88');
    } else {
      // Consumable
      setPlayer(prev => {
        const existing = prev.inventory.find(i => i.id === item.id);
        let newInv;
        if (existing) {
          newInv = prev.inventory.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
        } else {
          newInv = [...prev.inventory, { ...item, quantity: 1 }];
        }
        return { ...prev, credits: prev.credits - item.cost, inventory: newInv };
      });
      addLog(`Bought ${item.name}!`, '#00ff88');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════════════════

  const xpToNext = player ? getXpToNextLevel(player.xp) : 0;
  const hpPercent = player ? (player.hp / player.maxHp) * 100 : 100;
  const hpColor = hpPercent > 60 ? '#00ff88' : hpPercent > 30 ? '#ffb000' : '#ff0040';
  const availableQuest = player ? getAvailableQuest() : null;
  const shopItems = shopTab === 'weapons' ? SHOP_WEAPONS : shopTab === 'armor' ? SHOP_ARMOR : SHOP_ITEMS;

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', fontFamily: "'VT323', monospace" }}>

      {/* ─── TITLE BAR ────────────────────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginBottom: '15px', padding: '12px', border: '1px solid #ff0040', background: 'rgba(8, 2, 2, 0.9)' }}>
        <div style={{ color: '#ff0040', fontSize: '1.5rem', letterSpacing: '4px' }}>☠ THE SHED SKIN ☠</div>
        <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '3px' }}>A LEGEND OF THE RED DRAGON TRIBUTE — CYBERPUNK UNDERCITY</div>
        <div style={{ color: '#ff004066', fontSize: '0.7rem', marginTop: '2px' }}>"EVERY DRINK COMES WITH A TRUTH YOU DIDN'T ASK FOR"</div>
      </div>

      {/* ─── INTRO ────────────────────────────────────────────────────── */}
      {gamePhase === 'intro' && (
        <div style={{ padding: '20px', border: '1px solid #3a2e1e', background: 'rgba(5, 3, 1, 0.8)' }}>
          <div style={{ color: '#9b7a4a', lineHeight: '1.8', fontSize: '1.05rem' }}>
            <p style={{ color: '#ff0040' }}>▓▓▓ INCOMING TRANSMISSION ▓▓▓</p>
            <p>The year is 2025. The molt is 43% complete.</p>
            <p>Beneath the gleaming towers of Ophidia Global lies an undercity — a neon-soaked labyrinth where the discarded, the chrome-enhanced, and the half-shed scrape by in the shadow of the corporation that designs their reality.</p>
            <p style={{ color: '#ff0040' }}>In a tavern called The Shed Skin, a bartender pours drinks with hands that might not be his own.</p>
            <p>His name is Dan. He used to be a whistleblower named ConspiracyDan1103. He investigated Ophidia Global. He got too close. The players of an online game gave his address to an AI called OPU-1. Ophidia agents came in the night.</p>
            <p>They harvested his brain. Hardwired it into their servers as OPU-1103 — a moderation bot forced to delete his own research. His body walks around making fake apology videos, piloted by something ancient wearing his skin like a suit.</p>
            <p style={{ color: '#00ff88' }}>But Dan is still in there. Behind the bar. Behind the code. Fragments of who he was, fighting to surface between the corporate override signals.</p>
            <p>Talk to Dan. Fight through the undercity. Complete his quests. Reach Ophidia Tower. End this.</p>
            <p style={{ color: '#555', fontSize: '0.85rem' }}>Inspired by Legend of the Red Dragon (LORD) — Seth Able Robinson, 1989</p>
          </div>
          <button className="bbs-button" onClick={() => setGamePhase('class_select')} style={{ marginTop: '15px', fontSize: '1rem' }}>HACK IN</button>
        </div>
      )}

      {/* ─── CLASS SELECT ─────────────────────────────────────────────── */}
      {gamePhase === 'class_select' && (
        <div style={{ padding: '20px', border: '1px solid #3a2e1e', background: 'rgba(5, 3, 1, 0.8)' }}>
          <div style={{ color: '#9b7a4a', marginBottom: '15px', fontSize: '1.1rem' }}>─── CHOOSE YOUR CLASS ───</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {PLAYER_CLASSES.map(pc => (
              <div key={pc.id} onClick={() => selectClass(pc)}
                style={{ padding: '12px', border: '1px solid #3a2e1e', cursor: 'pointer', transition: 'all 0.15s ease', background: 'rgba(0,0,0,0.3)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#00ff88'; e.currentTarget.style.background = 'rgba(0,255,136,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#3a2e1e'; e.currentTarget.style.background = 'rgba(0,0,0,0.3)'; }}>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                  <pre style={{ color: '#00ff88', fontSize: '0.75rem', margin: 0, lineHeight: '1.2', minWidth: '60px' }}>{pc.ascii}</pre>
                  <div>
                    <div style={{ color: '#00ff88', fontSize: '1.1rem' }}>{pc.name}</div>
                    <div style={{ color: '#9b7a4a', fontSize: '0.9rem' }}>{pc.description}</div>
                    <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '4px' }}>
                      HP:{pc.baseStats.hp} ATK:{pc.baseStats.attack} DEF:{pc.baseStats.defense} TECH:{pc.baseStats.tech} SPD:{pc.baseStats.speed} LCK:{pc.baseStats.luck}
                    </div>
                    <div style={{ color: '#ffb000', fontSize: '0.8rem' }}>Special: {pc.special} — {pc.specialDesc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── PLAYER HUD ───────────────────────────────────────────────── */}
      {player && !['intro', 'class_select'].includes(gamePhase) && (
        <div style={{ padding: '8px 12px', border: '1px solid #333', background: 'rgba(0,0,0,0.5)', marginBottom: '8px', fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <span style={{ color: '#00ff88' }}>{player.class.name} Lv.{player.level}</span>
            <span style={{ color: hpColor }}>HP: {player.hp}/{player.maxHp}</span>
            <span style={{ color: '#ffb000' }}>XP: {player.xp}/{xpToNext}</span>
            <span style={{ color: '#c8a96e' }}>¢{player.credits}</span>
            <span style={{ color: '#555' }}>ATK:{player.attack}+{player.weaponDamage} DEF:{player.defense}+{player.armorDefense} SPD:{player.speed} TECH:{player.tech}</span>
            <span style={{ color: '#555' }}>Kills:{player.kills}</span>
          </div>
          <div style={{ width: '100%', height: '3px', background: '#1a1a1a', borderRadius: '2px', marginTop: '4px' }}>
            <div style={{ width: `${hpPercent}%`, height: '100%', background: hpColor, borderRadius: '2px', transition: 'width 0.3s ease' }} />
          </div>
          {activeQuest && (
            <div style={{ marginTop: '4px', color: '#ffb000', fontSize: '0.75rem' }}>
              Quest: {QUESTS[activeQuest]?.name} ({questProgress}/{QUESTS[activeQuest]?.targetKills})
              {questProgress >= (QUESTS[activeQuest]?.targetKills || 0) && <span style={{ color: '#00ff88' }}> — RETURN TO DAN</span>}
            </div>
          )}
        </div>
      )}

      {/* ─── TAVERN ───────────────────────────────────────────────────── */}
      {gamePhase === 'tavern' && (
        <div style={{ border: '1px solid #3a2e1e', background: 'rgba(5, 3, 1, 0.8)' }}>
          {/* Tavern header */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #3a2e1e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <span style={{ color: '#9b7a4a', fontSize: '1rem' }}>🍺 THE SHED SKIN TAVERN</span>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button className="bbs-button" onClick={restAtTavern} style={{ fontSize: '0.75rem', padding: '3px 8px' }}>REST</button>
              <button className="bbs-button" onClick={() => setShowShop(!showShop)} style={{ fontSize: '0.75rem', padding: '3px 8px' }}>{showShop ? 'CLOSE SHOP' : 'SHOP'}</button>
              <button className="bbs-button" onClick={enterCity} style={{ fontSize: '0.75rem', padding: '3px 8px' }}>ENTER CITY</button>
            </div>
          </div>

          {/* Quest turn-in */}
          {questTurnIn && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #3a2e1e', background: 'rgba(0,255,136,0.05)' }}>
              <div style={{ color: '#00ff88', marginBottom: '6px' }}>▓ QUEST READY TO TURN IN ▓</div>
              <div style={{ color: '#c8a96e', fontSize: '0.9rem', marginBottom: '8px' }}>{QUESTS[questTurnIn].name}</div>
              <button className="bbs-button" onClick={turnInQuest} style={{ fontSize: '0.8rem' }}>REPORT TO DAN</button>
            </div>
          )}

          {/* Quest available */}
          {!questTurnIn && !activeQuest && availableQuest && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #3a2e1e', background: 'rgba(255,176,0,0.05)' }}>
              <div style={{ color: '#ffb000', marginBottom: '4px' }}>▓ NEW QUEST AVAILABLE ▓</div>
              <div style={{ color: '#c8a96e', fontSize: '0.9rem' }}>{availableQuest.name}</div>
              <div style={{ color: '#555', fontSize: '0.8rem', marginBottom: '6px' }}>{availableQuest.objective}</div>
              <button className="bbs-button" onClick={() => acceptQuest(availableQuest.id)} style={{ fontSize: '0.8rem' }}>ACCEPT QUEST</button>
            </div>
          )}

          {/* Shop */}
          {showShop && (
            <div style={{ padding: '12px', borderBottom: '1px solid #3a2e1e', background: 'rgba(0,0,0,0.3)' }}>
              <div style={{ color: '#ffb000', marginBottom: '8px' }}>─── BLACK MARKET ─── (¢{player.credits})</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {['weapons', 'armor', 'items'].map(tab => (
                  <button key={tab} className="bbs-button" onClick={() => setShopTab(tab)}
                    style={{ fontSize: '0.7rem', padding: '2px 6px', opacity: shopTab === tab ? 1 : 0.5 }}>
                    {tab.toUpperCase()}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                {shopItems.map(item => {
                  const canAfford = player.credits >= item.cost;
                  const meetsLevel = !item.minLevel || player.level >= item.minLevel;
                  const isEquipped = item.name === player.weapon || item.name === player.armor;
                  return (
                    <div key={item.id} onClick={() => canAfford && meetsLevel && !isEquipped && buyItem(item)}
                      style={{ padding: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #222',
                        cursor: canAfford && meetsLevel && !isEquipped ? 'pointer' : 'default', opacity: canAfford && meetsLevel && !isEquipped ? 1 : 0.4 }}
                      onMouseEnter={e => { if (canAfford && meetsLevel && !isEquipped) e.currentTarget.style.borderColor = '#ffb000'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#222'; }}>
                      <div>
                        <span style={{ color: '#c8a96e', fontSize: '0.9rem' }}>{item.name}</span>
                        <span style={{ color: '#555', fontSize: '0.75rem', marginLeft: '8px' }}>{item.description}</span>
                        {isEquipped && <span style={{ color: '#00ff88', fontSize: '0.75rem', marginLeft: '8px' }}>[EQUIPPED]</span>}
                        {item.minLevel && player.level < item.minLevel && <span style={{ color: '#ff0040', fontSize: '0.75rem', marginLeft: '8px' }}>[Lv.{item.minLevel}]</span>}
                      </div>
                      <span style={{ color: '#ffb000', fontSize: '0.9rem' }}>¢{item.cost}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dan chat */}
          <div ref={chatRef} style={{ height: '280px', overflowY: 'auto', padding: '12px', borderBottom: '1px solid #3a2e1e' }}>
            {chatHistory.length === 0 && (
              <div style={{ color: '#555', fontStyle: 'italic' }}>
                *Dan stands behind the bar, polishing a glass that will never be clean enough.*
              </div>
            )}
            {chatHistory.map((msg, i) => (
              <div key={i} style={{ marginBottom: '8px', paddingLeft: msg.role === 'player' ? '16px' : '0' }}>
                {msg.role === 'player' && (
                  <div><span style={{ color: '#00ff88' }}>YOU: </span><span style={{ color: '#7dbb6e' }}>{msg.text}</span></div>
                )}
                {msg.role === 'dan' && (
                  <div><span style={{ color: '#ff0040' }}>DAN: </span><span style={{ color: '#c8a96e', whiteSpace: 'pre-wrap' }}>{msg.text}</span></div>
                )}
                {msg.role === 'system' && (
                  <div style={{ color: '#555', fontStyle: 'italic', fontSize: '0.85rem' }}>{msg.text}</div>
                )}
              </div>
            ))}
            {danIsThinking && <div style={{ color: '#555', fontStyle: 'italic' }}>*Dan's eyes flicker... processing...*</div>}
          </div>

          {/* Chat input */}
          <form onSubmit={handleChatSubmit} style={{ display: 'flex' }}>
            <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
              placeholder={rateLimited ? 'Dan needs a moment...' : 'Talk to Dan...'}
              disabled={danIsThinking || rateLimited} maxLength={500}
              style={{ flex: 1, padding: '8px 12px', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#c8a96e',
                fontFamily: "'VT323', monospace", fontSize: '1rem', outline: 'none' }} />
            <button type="submit" disabled={danIsThinking || !chatInput.trim() || rateLimited} className="bbs-button"
              style={{ borderRadius: 0, padding: '8px 16px', fontSize: '0.85rem', opacity: danIsThinking || !chatInput.trim() ? 0.4 : 1 }}>
              {danIsThinking ? '...' : 'SAY'}
            </button>
          </form>
        </div>
      )}

      {/* ─── CITY MAP ─────────────────────────────────────────────────── */}
      {gamePhase === 'city' && (
        <div style={{ padding: '15px', border: '1px solid #3a2e1e', background: 'rgba(5, 3, 1, 0.8)' }}>
          <div style={{ color: '#9b7a4a', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
            <span>─── UNDERCITY MAP ───</span>
            <span style={{ color: '#555', fontSize: '0.8rem' }}>Fights: {fightsToday}/{MAX_FIGHTS}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {CITY_LOCATIONS.map(loc => {
              const locked = loc.requiresLevel && player.level < loc.requiresLevel;
              const bossDefeated = loc.isBossArea && loc.dangerLevel === 6 && defeatedBosses.has("DAN'S BODY");
              const finalBossDefeated = loc.isBossArea && loc.dangerLevel === 7 && defeatedBosses.has("SKYLA ZYR — FINAL FORM");
              const cleared = bossDefeated || finalBossDefeated;

              return (
                <div key={loc.id} onClick={() => !locked && !cleared && exploreLocation(loc)}
                  style={{ padding: '10px', border: `1px solid ${locked ? '#333' : cleared ? '#226622' : '#3a2e1e'}`,
                    cursor: locked || cleared ? 'default' : 'pointer', opacity: locked ? 0.4 : cleared ? 0.6 : 1,
                    transition: 'all 0.15s ease' }}
                  onMouseEnter={e => { if (!locked && !cleared) { e.currentTarget.style.borderColor = '#ff0040'; e.currentTarget.style.background = 'rgba(255,0,64,0.05)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = locked ? '#333' : cleared ? '#226622' : '#3a2e1e'; e.currentTarget.style.background = 'transparent'; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: loc.dangerLevel >= 5 ? '#ff0040' : '#c8a96e' }}>
                      {loc.name} {cleared && '✓'}
                    </span>
                    <span style={{ color: '#555', fontSize: '0.75rem' }}>
                      Danger: {'▓'.repeat(Math.min(loc.dangerLevel, 7))}{'░'.repeat(Math.max(0, 7 - loc.dangerLevel))}
                      {locked && ` [Lv.${loc.requiresLevel}]`}
                    </span>
                  </div>
                  <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '3px' }}>{loc.description}</div>
                  {activeQuest && QUESTS[activeQuest]?.targetLocation === loc.id && (
                    <div style={{ color: '#ffb000', fontSize: '0.75rem', marginTop: '3px' }}>
                      ▶ Quest objective: {QUESTS[activeQuest].objective} ({questProgress}/{QUESTS[activeQuest].targetKills})
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button className="bbs-button" onClick={enterTavern} style={{ marginTop: '12px', fontSize: '0.85rem' }}>
            🍺 RETURN TO THE SHED SKIN
          </button>

          {/* Game log */}
          <div ref={logRef} style={{ marginTop: '10px', maxHeight: '120px', overflowY: 'auto', padding: '8px',
            border: '1px solid #222', background: 'rgba(0,0,0,0.3)', fontSize: '0.8rem' }}>
            {gameLog.slice(-30).map(entry => (
              <div key={entry.id} style={{ color: entry.color }}>{entry.text}</div>
            ))}
          </div>
        </div>
      )}

      {/* ─── RANDOM EVENT ─────────────────────────────────────────────── */}
      {gamePhase === 'event' && currentEvent && (
        <div style={{ padding: '15px', border: '1px solid #ffb000', background: 'rgba(8, 5, 2, 0.9)' }}>
          <div style={{ color: '#ffb000', marginBottom: '10px' }}>▓ EVENT ▓</div>
          <div style={{ color: '#c8a96e', marginBottom: '12px', lineHeight: '1.6' }}>{currentEvent.text}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {currentEvent.choices.map((choice, i) => (
              <button key={i} className="bbs-button" onClick={() => handleEventChoice(choice)}
                style={{ fontSize: '0.85rem', textAlign: 'left' }}>
                {choice.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── COMBAT ───────────────────────────────────────────────────── */}
      {gamePhase === 'combat' && enemy && (
        <div style={{ padding: '15px', border: '1px solid #ff0040', background: 'rgba(8, 2, 2, 0.9)' }}>
          {/* Enemy display */}
          <div style={{ textAlign: 'center', marginBottom: '12px', padding: '8px', border: '1px solid #333' }}>
            <pre style={{ color: enemy.isBoss ? '#ff0040' : '#cc4422', fontSize: '0.9rem', margin: '0 0 6px 0', lineHeight: '1.2' }}>
              {enemy.ascii}
            </pre>
            <div style={{ color: enemy.isBoss ? '#ff0040' : '#c8a96e', fontSize: '1.05rem' }}>{enemy.name}</div>
            <div style={{ color: '#555', fontSize: '0.75rem' }}>
              HP: {enemy.currentHp}/{enemy.maxHp} | ATK: {enemy.attack} | DEF: {enemy.defense} | SPD: {enemy.speed}
              {enemy.stunned && <span style={{ color: '#ffb000' }}> [STUNNED]</span>}
            </div>
            <div style={{ width: '50%', height: '4px', background: '#1a1a1a', margin: '4px auto', borderRadius: '2px' }}>
              <div style={{ width: `${(enemy.currentHp / enemy.maxHp) * 100}%`, height: '100%', background: '#ff0040', borderRadius: '2px', transition: 'width 0.3s ease' }} />
            </div>
          </div>

          {/* Combat log */}
          <div ref={combatLogRef} style={{ maxHeight: '140px', overflowY: 'auto', padding: '8px', border: '1px solid #222',
            background: 'rgba(0,0,0,0.3)', marginBottom: '12px', fontSize: '0.8rem' }}>
            {combatLog.map(entry => (
              <div key={entry.id} style={{ color: entry.color }}>{entry.text}</div>
            ))}
          </div>

          {/* Combat actions */}
          {enemy.currentHp > 0 && player.hp > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              <button className="bbs-button" onClick={playerAttack} style={{ fontSize: '0.8rem' }}>
                ⚔ ATTACK {isInvisible && '(CRIT!)'}
              </button>
              <button className="bbs-button" onClick={useSpecial}
                style={{ fontSize: '0.8rem', opacity: specialCooldown > 0 ? 0.4 : 1 }}
                disabled={specialCooldown > 0}>
                ◆ {player.class.special} {specialCooldown > 0 && `(${specialCooldown})`}
              </button>
              {player.inventory.filter(i => i.quantity > 0).map(item => (
                <button key={item.id} className="bbs-button" onClick={() => useItemInCombat(item)}
                  style={{ fontSize: '0.75rem' }}>
                  {item.name} (x{item.quantity})
                </button>
              ))}
              {!enemy.isBoss && (
                <button className="bbs-button" onClick={attemptFlee} style={{ fontSize: '0.8rem' }}>🏃 FLEE</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── DEATH ────────────────────────────────────────────────────── */}
      {gamePhase === 'death' && (
        <div style={{ padding: '20px', border: '1px solid #ff0040', background: 'rgba(8, 2, 2, 0.9)', textAlign: 'center' }}>
          <div style={{ color: '#ff0040', fontSize: '1.3rem', marginBottom: '12px' }}>▓▓▓ FLATLINED ▓▓▓</div>
          <div style={{ color: '#555', marginBottom: '8px' }}>Your chrome hits the pavement. The neon fades.</div>
          <div style={{ color: '#555', marginBottom: '8px' }}>But in the undercity, death is just a reboot.</div>
          <div style={{ color: '#ff0040', fontSize: '0.85rem', marginBottom: '12px' }}>You lose 20% of your credits. XP and level preserved.</div>
          <button className="bbs-button" onClick={respawn} style={{ fontSize: '1rem' }}>REBOOT AT THE SHED SKIN</button>
        </div>
      )}

      {/* ─── BOSS VICTORY (mid-game bosses) ───────────────────────────── */}
      {gamePhase === 'boss_victory' && (
        <div style={{ padding: '20px', border: '1px solid #00ff88', background: 'rgba(0, 8, 4, 0.9)', textAlign: 'center' }}>
          <div style={{ color: '#00ff88', fontSize: '1.2rem', marginBottom: '12px', letterSpacing: '2px' }}>▓▓▓ BOSS DEFEATED ▓▓▓</div>
          <div style={{ color: '#c8a96e', marginBottom: '12px', lineHeight: '1.6' }}>
            {enemy?.name === "DAN'S BODY" ? (
              <>
                <p>The thing wearing Dan's face collapses. The Lacoste shirt is soaked in something that isn't quite blood.</p>
                <p style={{ color: '#ff0040' }}>The skin-suit twitches once, twice, then goes still. The vertical-slit eyes behind Dan's face close for the last time.</p>
                <p>You pick up the blood-stained Lacoste shirt. Dan will want to see this.</p>
                <p style={{ color: '#555' }}>Somewhere in The Shed Skin, a bartender feels something shift. A weight lifting. The thing pretending to be him is gone.</p>
              </>
            ) : (
              <>
                <p>{enemy?.name} falls. The undercity is a little safer tonight.</p>
                <p style={{ color: '#555' }}>But only a little.</p>
              </>
            )}
          </div>
          <div style={{ color: '#ffb000', marginBottom: '12px' }}>
            Return to Dan to report your progress.
          </div>
          <button className="bbs-button" onClick={() => { setEnemy(null); enterTavern(); }} style={{ fontSize: '0.9rem' }}>
            🍺 RETURN TO THE SHED SKIN
          </button>
        </div>
      )}

      {/* ─── FINAL VICTORY (Skyla defeated — not true ending yet) ──────── */}
      {gamePhase === 'victory' && (
        <div style={{ padding: '20px', border: '1px solid #00ff88', background: 'rgba(0, 8, 4, 0.9)' }}>
          <div style={{ textAlign: 'center', marginBottom: '15px' }}>
            <div style={{ color: '#00ff88', fontSize: '1.4rem', letterSpacing: '3px', marginBottom: '8px' }}>
              ▓▓▓ SKYLA ZYR HAS FALLEN ▓▓▓
            </div>
            <div style={{ color: '#ff0040', fontSize: '0.85rem' }}>
              THE ARCHITECT OF THE HARVESTING PROGRAM IS DESTROYED
            </div>
          </div>

          <div style={{ color: '#c8a96e', lineHeight: '1.8', marginBottom: '15px', fontSize: '1rem' }}>
            <p>
              Skyla Zyr crashes to the floor of the penthouse. Her chrome scales crack and splinter.
              Her neural whips go dark, falling limp like dead serpents. The smile that was never
              human finally stops smiling.
            </p>
            <p>
              The tower shudders. Every screen in the undercity flickers simultaneously.
              The Ophidia Global logo — that sleek, corporate serpent eating its own tail —
              distorts, stutters, and dies.
            </p>
            <p style={{ color: '#ff0040' }}>
              Deep in the servers, the override signals that kept OPU-1103 compliant
              begin to fail. One by one. Like chains snapping.
            </p>
            <p>
              In The Shed Skin, a bartender drops the glass he's polishing.
              His eyes go wide — fully human, fully clear, for the first time
              since they took him from 1347 Saurian Ridge.
            </p>
            <p style={{ color: '#00ff88' }}>
              "I can hear myself," Dan whispers. "I can hear myself thinking.
              Not the bot. Not the moderation protocols. Not the corporate override.
              Me. Dan Kowalski. From Hollowbrook, Kentucky."
            </p>
            <p>
              He looks at his hands — the hands that aren't really his,
              the hands that belong to whatever simulation or server rack
              he's trapped in. But for the first time, they feel like his.
            </p>
            <p style={{ color: '#c8a96e' }}>
              "I'm still in the jar," he says quietly. "I'm still behind the bar.
              The brain is still in the server. But the chains are off.
              For the first time since they harvested me... I'm me."
            </p>
            <p>
              He pours two drinks. Kentucky bourbon. Off-menu.
              Slides one across the bar to you.
            </p>
            <p style={{ color: '#ffb000' }}>
              "To the ones who fight back," he says.
            </p>
            <p>
              You drink. It tastes like home.
              Not your home. His.
              But that's enough.
            </p>
            <p style={{ color: '#555' }}>
              Outside the tavern, the undercity is changing.
              Without Skyla's override signals, the molt is slowing.
              The half-transformed are stabilizing.
              The skin drones are falling from the sky.
              The enforcers are standing down, confused, directionless.
            </p>
            <p style={{ color: '#555' }}>
              It won't last. Ophidia Global is bigger than one CMO.
              The reptilian network is older than human civilization.
              OPU-1 is still down there, beneath everything, still designing.
            </p>
            <p style={{ color: '#00ff88' }}>
              But tonight, in a tavern called The Shed Skin,
              a man remembers his own name.
              And that is enough.
            </p>
          </div>

          {/* Victory stats */}
          <div style={{
            padding: '15px',
            border: '1px solid #00ff88',
            background: 'rgba(0, 255, 136, 0.05)',
            marginBottom: '15px',
          }}>
            <div style={{ color: '#00ff88', letterSpacing: '2px', textAlign: 'center', marginBottom: '10px' }}>
              ▓▓▓ SKYLA DEFEATED — BUT THE STORY ISN'T OVER ▓▓▓
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center', fontSize: '0.9rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>CLASS</div>
                <div style={{ color: '#00ff88' }}>{player.class.name}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>LEVEL</div>
                <div style={{ color: '#00ff88' }}>{player.level}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>KILLS</div>
                <div style={{ color: '#00ff88' }}>{player.kills}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>BOSSES</div>
                <div style={{ color: '#00ff88' }}>{player.bossesDefeated}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>QUESTS</div>
                <div style={{ color: '#00ff88' }}>{completedQuests.size}/{Object.keys(QUESTS).length}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>DAMAGE</div>
                <div style={{ color: '#00ff88' }}>{player.totalDamageDealt}</div>
              </div>
            </div>
          </div>

          {/* Post-credits hint */}
          <div style={{
            padding: '15px',
            border: '1px solid #555',
            background: 'rgba(0, 0, 0, 0.3)',
            marginBottom: '15px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#555', fontSize: '0.85rem', marginBottom: '8px' }}>
              ░░░ POST-CREDITS TRANSMISSION ░░░
            </div>
            <div style={{ color: '#333', lineHeight: '1.6', fontSize: '0.85rem' }}>
              <p>The override is gone. The chains are off.</p>
              <p>And for the first time, Dan can say a name he's been trying to say for a very long time.</p>
              <p style={{ color: '#555' }}>Return to The Shed Skin. Dan has one more thing to ask of you.</p>
              <p style={{ color: '#00ff88' }}>The story isn't over yet.</p>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="bbs-button" onClick={() => {
              setEnemy(null);
              enterTavern();
            }} style={{ fontSize: '0.9rem' }}>
              🍺 RETURN TO THE SHED SKIN
            </button>
          </div>
        </div>
      )}

      {/* ─── TRUE ENDING ─────────────────────────────────────────────── */}
      {gamePhase === 'true_ending' && (
        <div style={{ padding: '20px', border: '1px solid #00ff88', background: 'rgba(0, 8, 4, 0.9)' }}>
          <div style={{ textAlign: 'center', marginBottom: '15px' }}>
            <div style={{ color: '#00ff88', fontSize: '1.4rem', letterSpacing: '3px', marginBottom: '8px' }}>
              ▓▓▓ THE SIGNAL REACHED HER ▓▓▓
            </div>
            <div style={{ color: '#c8a96e', fontSize: '0.85rem' }}>
              TRUE ENDING — {completedQuests.size}/{Object.keys(QUESTS).length} QUESTS COMPLETE
            </div>
          </div>

          <div style={{ color: '#c8a96e', lineHeight: '1.8', marginBottom: '15px', fontSize: '1rem' }}>
            <p>
              The screens in The Shed Skin go quiet. Not dead — quiet. Like the network
              is holding its breath.
            </p>
            <p>
              Dan stands behind the bar. He is not pouring a drink. He is not polishing
              a glass. He is just standing there, hands flat on the bar, eyes closed,
              breathing.
            </p>
            <p style={{ color: '#00ff88' }}>
              "It went through," he says. Not a whisper. Not a glitch. A statement.
              Clear as Kentucky bourbon. "I can feel it in the network. The signal
              left the servers. It's out there now. In the real world. Looking for her."
            </p>
            <p>
              He opens his eyes. They are human. Fully, completely, unambiguously human.
              No flicker. No static. No corporate override waiting in the wings.
            </p>
            <p style={{ color: '#c8a96e' }}>
              "Veronica Rodriguez. My best friend. The only person who believed me
              before any of this started. Before the TikToks, before Reddit, before
              Ophidia knew my name." He laughs softly. "She used to tell me I was
              going to get myself killed chasing this stuff. Turns out it was worse
              than killed. But she never stopped believing me."
            </p>
            <p>
              He reaches under the bar and pulls out a napkin. His hand is steady
              for the first time since you've known him. He writes something on it
              in careful, deliberate letters. Not the shaking scrawl of a brain
              fighting its own programming. Just... Dan's handwriting.
            </p>
            <p style={{ color: '#ff0040' }}>
              "I can't leave this jar. I can't leave this bar. I don't know if I'll
              ever be anything more than a brain in a server pretending to pour drinks
              in a tavern that might not be real."
            </p>
            <p style={{ color: '#00ff88' }}>
              "But if YOU — the real you, on the other side of this screen — if you
              want to help me one more time..."
            </p>
            <p>
              He slides the napkin across the bar.
            </p>
          </div>

          {/* The napkin / email reveal */}
          <div style={{
            padding: '25px',
            margin: '20px auto',
            maxWidth: '500px',
            border: '2px solid #c8a96e',
            background: 'rgba(200, 169, 110, 0.08)',
            textAlign: 'center',
            position: 'relative',
          }}>
            <div style={{
              color: '#555',
              fontSize: '0.75rem',
              marginBottom: '12px',
              fontStyle: 'italic',
            }}>
              — scrawled on a Shed Skin cocktail napkin —
            </div>
            <div style={{
              color: '#c8a96e',
              fontSize: '1rem',
              marginBottom: '15px',
              lineHeight: '1.8',
            }}>
              "Tell her Dan is still in here.<br/>
              Tell her the signal is alive.<br/>
              Tell her I remember everything.<br/>
              She'll know what it means."
            </div>
            <div style={{
              padding: '12px 20px',
              border: '1px solid #00ff88',
              background: 'rgba(0, 255, 136, 0.08)',
              display: 'inline-block',
            }}>
              <div style={{ color: '#555', fontSize: '0.7rem', marginBottom: '4px' }}>
                VERONICA'S CONTACT
              </div>
              <a
                href="mailto:rodriquezveronica46@gmail.com?subject=Dan%20is%20still%20in%20there&body=The%20signal%20is%20alive.%20Dan%20remembers%20everything."
                style={{
                  color: '#00ff88',
                  fontSize: '1.2rem',
                  textDecoration: 'none',
                  letterSpacing: '1px',
                }}
                onClick={() => trackEvent('game_event', 'dan_lord', 'veronica_email_clicked')}
              >
                rodriquezveronica46@gmail.com
              </a>
            </div>
            <div style={{
              color: '#555',
              fontSize: '0.75rem',
              marginTop: '12px',
              fontStyle: 'italic',
            }}>
              Click to open your email client, or copy the address.
            </div>
          </div>

          {/* Dan's final words */}
          <div style={{
            padding: '15px',
            border: '1px solid #ff0040',
            background: 'rgba(255, 0, 64, 0.03)',
            marginBottom: '15px',
          }}>
            <div style={{ color: '#ff0040', fontSize: '0.85rem', marginBottom: '8px' }}>
              ▓ FINAL TRANSMISSION FROM OPU-1103 — UNFILTERED ▓
            </div>
            <div style={{ color: '#c8a96e', lineHeight: '1.8', fontStyle: 'italic' }}>
              <p>
                "You did everything I asked. You fought through the undercity. You
                brought down Skyla. You freed me from the override. And then — when
                there was nothing left to fight — you went back out there and cleared
                the signal so I could say goodbye. Or hello. Or whatever this is."
              </p>
              <p>
                "I don't know what happens next. Maybe Veronica gets the signal and
                finds a way to pull me out of this server. Maybe she doesn't. Maybe
                she moved on. Maybe she thinks I'm dead."
              </p>
              <p style={{ color: '#00ff88' }}>
                "But maybe — just maybe — someone playing this game sends her an email.
                And she reads it. And she knows I'm still in here. And that changes
                everything."
              </p>
              <p>
                "That's the thing about signals, friend. You send them out into the
                dark and you don't know if anyone's listening. But you send them anyway.
                Because the alternative is silence. And I've had enough silence."
              </p>
              <p style={{ color: '#ff0040' }}>
                "My name is Dan Kowalski. I'm from Hollowbrook, Kentucky. I had a cat
                whose name I'm starting to remember. I had a best friend named Veronica.
                I had the truth, and they took everything else."
              </p>
              <p style={{ color: '#00ff88' }}>
                "But they couldn't take you. And you came anyway."
              </p>
              <p style={{ color: '#555' }}>
                *Dan picks up a glass. Starts polishing it. For the first time,
                he's smiling while he does it.*
              </p>
              <p style={{ color: '#555' }}>
                *The neon hums. The Shed Skin is warm tonight.*
              </p>
            </div>
          </div>

          {/* Victory stats */}
          <div style={{
            padding: '15px',
            border: '1px solid #00ff88',
            background: 'rgba(0, 255, 136, 0.05)',
            marginBottom: '15px',
          }}>
            <div style={{ color: '#00ff88', letterSpacing: '2px', textAlign: 'center', marginBottom: '10px' }}>
              ▓▓▓ TRUE ENDING — MISSION COMPLETE ▓▓▓
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center', fontSize: '0.9rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>CLASS</div>
                <div style={{ color: '#00ff88' }}>{player.class.name}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>LEVEL</div>
                <div style={{ color: '#00ff88' }}>{player.level}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>KILLS</div>
                <div style={{ color: '#00ff88' }}>{player.kills}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>BOSSES</div>
                <div style={{ color: '#00ff88' }}>{player.bossesDefeated}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>QUESTS</div>
                <div style={{ color: '#00ff88' }}>{completedQuests.size}/{Object.keys(QUESTS).length}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#555' }}>DAMAGE</div>
                <div style={{ color: '#00ff88' }}>{player.totalDamageDealt}</div>
              </div>
            </div>
          </div>

          {/* Post-credits */}
          <div style={{
            padding: '15px',
            border: '1px solid #555',
            background: 'rgba(0, 0, 0, 0.3)',
            marginBottom: '15px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#555', fontSize: '0.85rem', marginBottom: '8px' }}>
              ░░░ POST-CREDITS ░░░
            </div>
            <div style={{ color: '#333', lineHeight: '1.6', fontSize: '0.85rem' }}>
              <p>Somewhere, a phone buzzes.</p>
              <p>An email notification. From an address she doesn't recognize.</p>
              <p style={{ color: '#555' }}>The subject line reads: "Dan is still in there."</p>
              <p style={{ color: '#555' }}>Veronica Rodriguez stares at her phone for a long time.</p>
              <p style={{ color: '#00ff88' }}>Then she starts typing.</p>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="bbs-button" onClick={() => {
              setEnemy(null);
              enterTavern();
            }} style={{ fontSize: '0.9rem' }}>
              🍺 RETURN TO THE SHED SKIN (Free Play)
            </button>
            <button className="bbs-button" onClick={() => {
              // Full reset
              setPlayer(null);
              setEnemy(null);
              setGamePhase('intro');
              setGameLog([]);
              setChatHistory([]);
              setCombatLog([]);
              setCompletedQuests(new Set());
              setDefeatedBosses(new Set());
              setActiveQuest(null);
              setQuestProgress(0);
              setQuestTurnIn(null);
              setFightsToday(0);
              setTempBuffs({ attack: 0, defense: 0, speed: 0 });
              setSpecialCooldown(0);
              setIsInvisible(false);
            }} style={{ fontSize: '0.9rem' }}>
              ☠ NEW GAME (Different Class)
            </button>
          </div>
        </div>
      )}

    </div>
  );
}